import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// ─── Responsive helper ─────────────────────────────
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

// ─── Config ─────────────────────────────
const DURATIONS = {
  wingo:  [{ s: 30, label: "30sec" }, { s: 60, label: "1 Min" }, { s: 180, label: "3 Min" }, { s: 300, label: "5 Min" }],
  trxwin: [{ s: 30, label: "30sec" }, { s: 60, label: "1 Min" }, { s: 180, label: "3 Min" }, { s: 300, label: "5 Min" }],
  k3:     [{ s: 60, label: "1 Min" }, { s: 180, label: "3 Min" }, { s: 300, label: "5 Min" }, { s: 600, label: "10 Min" }],
  "5d":   [{ s: 60, label: "1 Min" }, { s: 180, label: "3 Min" }, { s: 300, label: "5 Min" }, { s: 600, label: "10 Min" }],
};

const GAME_TITLES = { wingo: "WinGo", trxwin: "TRX Win", k3: "K3", "5d": "5D" };

const MULT_CHIPS = [
  { label: "Random", value: "rand" },
  { label: "X1", value: 1 },
  { label: "X5", value: 5 },
  { label: "X10", value: 10 },
  { label: "X20", value: 20 },
  { label: "X50", value: 50 },
  { label: "X100", value: 100 },
];

// Number → colour mapping (2-tone dots for 0/5)
const NUM_COLORS = {
  0: ["red", "violet"], 1: ["green"], 2: ["red"], 3: ["green"], 4: ["red"],
  5: ["green", "violet"], 6: ["red"], 7: ["green"], 8: ["red"], 9: ["green"],
};

function pad(n, w = 2) { return String(n).padStart(w, "0"); }

// ─── Web Audio sound effects (no external files) ───────────────────
const audioCtxRef = { current: null };
function getAC() {
  if (!audioCtxRef.current) {
    try { audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); }
    catch { return null; }
  }
  return audioCtxRef.current;
}
function playTone(freq, dur = 0.15, type = "sine", vol = 0.15) {
  const ac = getAC(); if (!ac) return;
  try {
    const o = ac.createOscillator(); const g = ac.createGain();
    o.type = type; o.frequency.value = freq;
    g.gain.value = vol;
    o.connect(g); g.connect(ac.destination);
    o.start();
    g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + dur);
    o.stop(ac.currentTime + dur);
  } catch { /* ignore */ }
}
function sfxBet()  { playTone(880, 0.08, "square", 0.10); setTimeout(() => playTone(1320, 0.10, "square", 0.10), 60); }
function sfxWin()  { [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => playTone(f, 0.18, "sine", 0.14), i * 90)); }
function sfxLose() { playTone(220, 0.35, "sawtooth", 0.10); setTimeout(() => playTone(160, 0.35, "sawtooth", 0.10), 180); }
function sfxTap()  { playTone(1200, 0.04, "triangle", 0.06); }
// Tick beep for last-5s countdown: two-tone (higher on final 3 secs)
function sfxTick(t) {
  // t = seconds remaining (5,4,3,2,1)
  const freq = t <= 3 ? 1400 : 1000;
  playTone(freq, 0.09, "square", 0.13);
}

// ─── How to Play modal ──────────────────────────────────────────
function HowToPlayModal({ open, onClose, game }) {
  if (!open) return null;
  // rules structured with accent color + big multiplier badge
  const RULES = {
    wingo: [
      { label: "Green · সবুজ", desc: "ফলাফলের সংখ্যাটি 1, 3, 7 বা 9 এলে ২× জয় · সংখ্যাটি 5 হলে ১.৫× জয়।", mult: "2× / 1.5×", accent: "#16a34a", bg: "#dcfce7" },
      { label: "Red · লাল", desc: "ফলাফলের সংখ্যাটি 2, 4, 6 বা 8 এলে ২× জয় · সংখ্যাটি 0 হলে ১.৫× জয়।", mult: "2× / 1.5×", accent: "#ef4444", bg: "#fee2e2" },
      { label: "Violet · বেগুনি", desc: "ফলাফলের সংখ্যাটি 0 বা 5 হলে বিশেষ ৪.৫× জয়।", mult: "4.5×", accent: "#a855f7", bg: "#f3e8ff" },
      { label: "Number · সংখ্যা (0-9)", desc: "সঠিক সংখ্যাটি হুবহু মিলে গেলে সর্বোচ্চ ৯× জয়।", mult: "9×", accent: "#e11d48", bg: "#ffe4e6" },
      { label: "Big · বড় (5-9)", desc: "ফলাফল 5, 6, 7, 8 বা 9-এর যেকোনো একটি হলে ২× জয়।", mult: "2×", accent: "#f59e0b", bg: "#fef3c7" },
      { label: "Small · ছোট (0-4)", desc: "ফলাফল 0, 1, 2, 3 বা 4-এর যেকোনো একটি হলে ২× জয়।", mult: "2×", accent: "#3b82f6", bg: "#dbeafe" },
    ],
    trxwin: [
      { label: "Green / Red / Violet · সবুজ / লাল / বেগুনি", desc: "WinGo-এর অনুরূপ নিয়ম; ফলাফল TRON ব্লক হ্যাশ থেকে নির্ধারিত হয় — সম্পূর্ণ যাচাইযোগ্য।", mult: "2× / 1.5× / 4.5×", accent: "#a855f7", bg: "#f3e8ff" },
      { label: "Number · সংখ্যা (0-9)", desc: "সঠিক সংখ্যাটি মিলে গেলে ৯× জয়।", mult: "9×", accent: "#e11d48", bg: "#ffe4e6" },
      { label: "Big / Small · বড় / ছোট", desc: "সঠিক অনুমান করলে ২× জয়।", mult: "2×", accent: "#f59e0b", bg: "#fef3c7" },
    ],
    k3: [
      { label: "Sum · যোগফল (3-18)", desc: "তিন ডাইসের যোগফল অনুমান করুন — সম্ভাবনা অনুযায়ী ৬× থেকে ২০০× পর্যন্ত জয়।", mult: "6× — 200×", accent: "#e11d48", bg: "#ffe4e6" },
      { label: "Big · বড় (11-17) / Small · ছোট (4-10)", desc: "যোগফল বড় না ছোট অনুমান করুন। তিন ডাইস একই মান দেখালে বাজি বাতিল।", mult: "2×", accent: "#f59e0b", bg: "#fef3c7" },
      { label: "Odd / Even · বিজোড় / জোড়", desc: "যোগফল বিজোড় নাকি জোড় অনুমান করুন। তিন ডাইস একই মান দেখালে বাজি বাতিল।", mult: "2×", accent: "#16a34a", bg: "#dcfce7" },
      { label: "Any Triple · যেকোনো ট্রিপল", desc: "তিন ডাইসের মান একই হলে (যেমন 4-4-4) ৩৪.৫× জয়।", mult: "34.5×", accent: "#a855f7", bg: "#f3e8ff" },
      { label: "Specific Triple · নির্দিষ্ট ট্রিপল (111 – 666)", desc: "উল্লিখিত নির্দিষ্ট ট্রিপলটি মিলে গেলে বিশাল ২০৭× জয়।", mult: "207×", accent: "#dc2626", bg: "#fee2e2" },
      { label: "Any Pair · যেকোনো জোড়া", desc: "যেকোনো দুটি ডাইসের মান একই হলে ১৩.৮৩× জয়।", mult: "13.83×", accent: "#3b82f6", bg: "#dbeafe" },
    ],
    "5d": [
      { label: "Position · অবস্থান A/B/C/D/E : সংখ্যা", desc: "নির্বাচিত অবস্থানে সঠিক সংখ্যা এলে ৯× জয়।", mult: "9×", accent: "#e11d48", bg: "#ffe4e6" },
      { label: "Position Big / Small · অবস্থান বড় / ছোট", desc: "নির্বাচিত অবস্থানের সংখ্যা 5-9 (বড়) বা 0-4 (ছোট) মিললে ২× জয়।", mult: "2×", accent: "#f59e0b", bg: "#fef3c7" },
      { label: "Position Odd / Even · অবস্থান বিজোড় / জোড়", desc: "নির্বাচিত অবস্থানের সংখ্যা বিজোড় বা জোড় মিলে গেলে ২× জয়।", mult: "2×", accent: "#16a34a", bg: "#dcfce7" },
      { label: "Sum Big (23-45) / Small (0-22) · যোগফল বড় / ছোট", desc: "পাঁচ অঙ্কের যোগফল বড় বা ছোট অনুমান করুন।", mult: "2×", accent: "#3b82f6", bg: "#dbeafe" },
      { label: "Sum Odd / Even · যোগফল বিজোড় / জোড়", desc: "পাঁচ অঙ্কের যোগফল বিজোড় বা জোড় মিলে গেলে ২× জয়।", mult: "2×", accent: "#a855f7", bg: "#f3e8ff" },
    ],
  };
  const rules = RULES[game] || [];
  const gameTitle = GAME_TITLES[game] || "";
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-[popup_.35s_cubic-bezier(.34,1.56,.64,1)]" onClick={onClose} data-testid="howtoplay-modal">
      <div className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}
           style={{ fontFamily: "'Hind Siliguri', 'Noto Sans Bengali', 'Inter', system-ui, sans-serif" }}>
        {/* HEADER with rocket badge */}
        <div className="relative overflow-hidden px-5 pt-5 pb-6 text-white"
             style={{ background: "linear-gradient(135deg, #ff4d67 0%, #ff8fa2 60%, #ffb1bd 100%)" }}>
          <div className="absolute -right-4 -top-6 w-32 h-32 rounded-full bg-white/15 blur-xl pointer-events-none" />
          <div className="absolute -left-6 bottom-0 w-24 h-24 rounded-full bg-white/10 blur-xl pointer-events-none" />
          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white/25 flex items-center justify-center shadow-inner ring-2 ring-white/50">
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                  <path d="M12 2l2.4 6.8 7.1.5-5.6 4.6 1.9 7-5.8-4.3-5.8 4.3 1.9-7L2.5 9.3l7.1-.5z"/>
                </svg>
              </div>
              <div>
                <div className="font-black text-xl leading-tight">কীভাবে খেলবেন</div>
                <div className="text-white/90 text-[11.5px] font-semibold tracking-wide mt-0.5">HOW TO PLAY · {gameTitle.toUpperCase()}</div>
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/25 hover:bg-white/35 text-white text-xl font-bold flex items-center justify-center transition" data-testid="howtoplay-close">
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18"/>
              </svg>
            </button>
          </div>
          <div className="relative mt-4 flex items-center gap-2 text-white/95 text-[12.5px]">
            <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
              সরাসরি ফলাফল
            </div>
            <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur px-2.5 py-1 rounded-full">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 8v4l3 2" strokeLinecap="round"/><circle cx="12" cy="12" r="9"/></svg>
              তাৎক্ষণিক সেটেলমেন্ট
            </div>
          </div>
        </div>

        {/* RULES LIST */}
        <div className="max-h-[62vh] overflow-y-auto p-4 space-y-2.5 bg-gradient-to-b from-white to-rose-50/40">
          <div className="text-[11px] font-black uppercase tracking-widest text-slate-400 px-1 mb-1">বাজির নিয়মাবলী · Betting Rules</div>
          {rules.map((r, i) => (
            <div key={i}
                 className="relative flex items-start gap-3 pl-3 pr-3 py-3 rounded-2xl border border-slate-100 bg-white hover:shadow-md transition"
                 style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
              {/* Left accent strip */}
              <div className="absolute top-2 bottom-2 left-0 w-1 rounded-full" style={{ background: r.accent }} />
              {/* Icon dot */}
              <div className="flex-shrink-0 mt-0.5 w-9 h-9 rounded-full flex items-center justify-center shadow-inner"
                   style={{ background: r.bg, color: r.accent }}>
                <span className="font-black text-sm">{i + 1}</span>
              </div>
              {/* Text */}
              <div className="flex-1 min-w-0">
                <div className="font-black text-[13.5px] leading-tight" style={{ color: r.accent }}>
                  {r.label}
                </div>
                <div className="text-slate-700 text-[12.5px] leading-relaxed mt-1">{r.desc}</div>
              </div>
              {/* Multiplier badge */}
              <div className="flex-shrink-0 self-center">
                <div className="px-2.5 py-1 rounded-lg text-[11.5px] font-black text-white whitespace-nowrap shadow"
                     style={{ background: `linear-gradient(180deg, ${r.accent}, ${r.accent}dd)` }}>
                  {r.mult}
                </div>
              </div>
            </div>
          ))}

          {/* Important rules callout */}
          <div className="mt-4 rounded-2xl p-4 border border-amber-200"
               style={{ background: "linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%)" }}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center shadow">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                  <path d="M12 8v4M12 16.01l.01-.011"/>
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                </svg>
              </div>
              <div className="font-black text-amber-800 text-sm">গুরুত্বপূর্ণ নিয়মাবলী</div>
            </div>
            <ul className="space-y-1.5 text-[12.5px] text-amber-900/90 leading-relaxed">
              <li className="flex gap-2"><span className="text-amber-600 font-black">›</span> প্রতিটি রাউন্ড শেষ হওয়ার <b>১০ সেকেন্ড আগে</b> বাজি বন্ধ হয়ে যায়।</li>
              <li className="flex gap-2"><span className="text-amber-600 font-black">›</span> সঠিক অনুমান হলে জয়ের অর্থ সরাসরি আপনার <b>BDT ওয়ালেটে</b> ক্রেডিট হবে।</li>
              <li className="flex gap-2"><span className="text-amber-600 font-black">›</span> ফলাফল সার্ভার-জেনারেটেড এবং প্রকাশের পর <b>অপরিবর্তনীয়</b>।</li>
              <li className="flex gap-2"><span className="text-amber-600 font-black">›</span> <b>দায়িত্বশীলভাবে খেলুন</b> — সামর্থ্যের বাইরে বাজি ধরবেন না।</li>
            </ul>
          </div>

          <div className="text-center text-[11px] text-slate-400 pt-2 pb-1">
            © Maxlive88 · Play Smart, Win Big
          </div>
        </div>

        {/* Bottom close button */}
        <div className="px-4 pb-4 pt-2 bg-white">
          <button onClick={onClose}
                  className="w-full py-3 rounded-2xl font-black text-white text-sm shadow-lg tracking-wide"
                  style={{ background: "linear-gradient(180deg, #ff4d67 0%, #ff7a8c 100%)" }}
                  data-testid="howtoplay-ok">
            বুঝেছি · Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function pad_placeholder(){}

// Format period as YYYYMMDD + duration + zero-padded index
function formatPeriod(round, game) {
  if (!round) return "—";
  const d = new Date(round.start_at);
  const y = d.getUTCFullYear();
  const mo = pad(d.getUTCMonth() + 1);
  const da = pad(d.getUTCDate());
  const durCode = { 30: "30", 60: "01", 180: "03", 300: "05", 600: "10" }[round.duration] || "00";
  const seq = String(round.period).slice(-7).padStart(7, "0");
  return `${y}${mo}${da}${durCode}${seq}`;
}

// Digital timer card
function TimerDigit({ d }) {
  return (
    <span className="inline-flex items-center justify-center w-7 h-9 sm:w-8 sm:h-10 rounded-md bg-white text-rose-500 font-black text-2xl sm:text-3xl shadow-sm mx-0.5">
      {d}
    </span>
  );
}

// Mini result ball — egg shape like reference
function MiniBall({ n }) {
  if (n === undefined || n === null) return null;
  const cols = NUM_COLORS[n] || ["gray"];
  const isTwoTone = cols.length === 2;
  const bg = isTwoTone
    ? cols[0] === "red"
      ? "linear-gradient(135deg, #ef4444 0%, #ef4444 50%, #a855f7 50%, #a855f7 100%)"
      : "linear-gradient(135deg, #16a34a 0%, #16a34a 50%, #a855f7 50%, #a855f7 100%)"
    : cols[0] === "red"
    ? "radial-gradient(circle at 30% 25%, #ffb1b8, #ef4444)"
    : cols[0] === "green"
    ? "radial-gradient(circle at 30% 25%, #a4f0b8, #16a34a)"
    : "radial-gradient(circle at 30% 25%, #d8b4fe, #a855f7)";
  const numColor = cols[0] === "red" ? "#ef4444" : "#16a34a";
  return (
    <span className="relative inline-flex items-center justify-center w-8 h-9 shadow-md" style={{ background: bg, borderRadius: "50% 50% 50% 50% / 55% 55% 45% 45%" }}>
      {/* Inner white oval */}
      <span className="absolute inset-[15%] bg-white flex items-center justify-center" style={{ borderRadius: "50% 50% 50% 50% / 55% 55% 45% 45%" }}>
        <span className="font-black text-xs" style={{ color: numColor }}>{n}</span>
      </span>
      {/* Highlight */}
      <span className="absolute pointer-events-none rounded-full" style={{
        top:'8%', left:'22%', width:'35%', height:'20%',
        background:'radial-gradient(ellipse at center, rgba(255,255,255,0.7) 0%, transparent 70%)',
        filter:'blur(1px)',
      }} />
    </span>
  );
}

// Big pill ball (0-9 grid) — chip style with notched edge like reference
function BigBall({ n, onClick, testid }) {
  const cols = NUM_COLORS[n];
  const isTwoTone = cols.length === 2;
  // Outer ring color(s)
  const outerFill = isTwoTone
    ? cols[0] === "red"
      ? "linear-gradient(90deg, #ff5a70 0%, #ff5a70 50%, #a855f7 50%, #a855f7 100%)"
      : "linear-gradient(90deg, #22c55e 0%, #22c55e 50%, #a855f7 50%, #a855f7 100%)"
    : cols[0] === "red"
    ? "radial-gradient(circle at 30% 30%, #ff8fa3, #ef4444 60%, #b91c1c)"
    : "radial-gradient(circle at 30% 30%, #86efac, #16a34a 60%, #15803d)";
  // Number text colour — matches the ring colour
  const numColor = isTwoTone
    ? cols[0] === "red" ? "#e11d48" : "#16a34a"
    : cols[0] === "red" ? "#dc2626" : "#16a34a";
  return (
    <button onClick={onClick} data-testid={testid}
      className="relative aspect-square active:scale-95 transition duration-150 tap-bounce"
    >
      {/* Notched outer ring (poker-chip style with 12 nubs) */}
      <div className="absolute inset-0" style={{
        WebkitMask: "radial-gradient(circle, black 62%, transparent 62%), conic-gradient(from 15deg, black 0 6%, transparent 6% 8.33%)",
        WebkitMaskComposite: "source-over",
        maskComposite: "add",
        borderRadius: "9999px",
        background: outerFill,
        filter: "drop-shadow(0 3px 4px rgba(0,0,0,0.25))",
      }} />
      {/* Simpler fallback outer ring for browsers that don't handle the mask combo */}
      <div className="absolute inset-0 rounded-full shadow-lg" style={{ background: outerFill }}>
        {/* 12 notches around the edge */}
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i / 12) * Math.PI * 2;
            const cx = 50 + Math.cos(a) * 46;
            const cy = 50 + Math.sin(a) * 46;
            return <circle key={i} cx={cx} cy={cy} r="4" fill="#fff" opacity="0.9" />;
          })}
        </svg>
      </div>
      {/* Inner white disc */}
      <div className="absolute inset-[18%] rounded-full bg-gradient-to-br from-white via-white to-slate-50 shadow-inner flex items-center justify-center">
        {/* Number */}
        <span className="font-black text-2xl sm:text-3xl" style={{ color: numColor, fontFamily: "Arial Rounded MT Bold, Arial, sans-serif" }}>{n}</span>
      </div>
      {/* Top glossy shine */}
      <span
        className="absolute rounded-full pointer-events-none"
        style={{
          top: '8%', left: '22%', width: '48%', height: '18%',
          background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.8) 0%, transparent 65%)',
          filter: 'blur(2px)',
        }}
      />
    </button>
  );
}

// TRX hash bubbles (F,F,E,8,D style)
function HashBubbles({ hash }) {
  if (!hash) {
    return (
      <div className="flex justify-center gap-1.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <span key={i} className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-gradient-to-br from-rose-300 to-rose-500 text-white font-black text-lg shadow ring-2 ring-white">
            ?
          </span>
        ))}
      </div>
    );
  }
  const chars = hash.slice(-5).toUpperCase().split("");
  return (
    <div className="flex justify-center gap-1.5">
      {chars.map((c, i) => (
        <span key={i}
          className={`inline-flex items-center justify-center w-11 h-11 rounded-full ${
            /\d/.test(c)
              ? "bg-gradient-to-br from-yellow-200 to-yellow-400 text-yellow-800"
              : "bg-gradient-to-br from-white to-rose-200 text-rose-600"
          } font-black text-lg shadow ring-2 ring-white`}
        >
          {c}
        </span>
      ))}
    </div>
  );
}

// ─── Bet dialog — orange ticket style matching reference ─────────────────
function BetDialog({ open, onClose, game, betType, betValue, label, mult, onConfirm, balance, gameLabel }) {
  const [chip, setChip] = useState(1);
  const [qty, setQty] = useState(1);
  const [multi, setMulti] = useState(1);
  const [agree, setAgree] = useState(true);
  useEffect(() => { if (open) { setChip(1); setQty(1); setMulti(1); setAgree(true); } }, [open]);
  if (!open) return null;
  const total = chip * qty * multi;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50"
      onClick={onClose}
      data-testid="bet-dialog"
    >
      <div className="w-full max-w-md bg-white rounded-t-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Orange top with select label ticket */}
        <div className="bg-[#ff9a4a] px-4 pt-3 pb-6 relative">
          <div className="text-center text-white font-bold text-sm mb-2">{gameLabel || "WinGo 30sec"}</div>
          <div className="bg-white rounded-lg py-3 text-center text-slate-800 font-bold text-lg shadow-inner">
            Select {label || betValue}
          </div>
          {/* Zigzag bottom */}
          <svg viewBox="0 0 400 20" className="absolute -bottom-px left-0 w-full h-5" preserveAspectRatio="none">
            <path d="M0,0 L0,10 L20,20 L40,10 L60,20 L80,10 L100,20 L120,10 L140,20 L160,10 L180,20 L200,10 L220,20 L240,10 L260,20 L280,10 L300,20 L320,10 L340,20 L360,10 L380,20 L400,10 L400,0 Z" fill="#ff9a4a" />
          </svg>
        </div>

        {/* Body */}
        <div className="px-4 py-4">
          {/* Wallet balance card */}
          <div className="mb-4 rounded-2xl p-3 flex items-center justify-between"
               style={{ background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)", border: "1px solid #fed7aa" }}>
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-inner ring-1 ring-orange-200">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 12V8H6a2 2 0 010-4h12v4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4 6v12a2 2 0 002 2h14v-4" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="17" cy="14" r="1.5" fill="currentColor"/>
                </svg>
              </div>
              <div className="leading-tight">
                <div className="text-slate-500 text-[10.5px] font-bold uppercase tracking-wider">Wallet Balance</div>
                <div className="text-orange-600 font-black text-lg" data-testid="bet-balance-display">
                  ৳{Number(balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-slate-500 text-[10.5px] font-bold uppercase tracking-wider">Available</div>
              <div className="inline-flex items-center gap-1 text-emerald-600 font-black text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Live
              </div>
            </div>
          </div>

          {/* Amount row */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-800 font-bold text-base">Amount</span>
            <div className="flex gap-2">
              {[1, 10, 100, 1000].map((a) => (
                <button key={a} onClick={() => setChip(a)}
                  className={`px-4 py-1.5 rounded-md font-bold text-sm ${
                    chip === a ? "bg-[#ff9a4a] text-white shadow" : "bg-slate-100 text-slate-500"
                  }`}
                  data-testid={`bet-amount-${a}`}
                >{a}</button>
              ))}
            </div>
          </div>

          {/* Quantity row */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-800 font-bold text-lg">Quantity</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-9 h-9 rounded bg-[#ff9a4a] text-white font-bold text-lg" data-testid="bet-qty-minus">−</button>
              <input type="number" value={qty}
                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1")))}
                className="w-16 text-center py-1.5 bg-slate-100 text-slate-800 font-bold outline-none rounded"
                data-testid="bet-qty-input"
              />
              <button onClick={() => setQty(qty + 1)}
                className="w-9 h-9 rounded bg-[#ff9a4a] text-white font-bold text-lg" data-testid="bet-qty-plus">+</button>
            </div>
          </div>

          {/* Multiplier row */}
          <div className="flex items-center justify-center gap-1.5 mb-4 flex-wrap">
            {[1, 5, 10, 20, 50, 100].map((m) => (
              <button key={m} onClick={() => setMulti(m)}
                className={`px-3 py-1.5 rounded text-sm font-bold ${
                  multi === m ? "bg-[#ff9a4a] text-white" : "bg-slate-100 text-slate-500"
                }`}
                data-testid={`bet-multi-${m}`}
              >X{m}</button>
            ))}
          </div>

          {/* Agree checkbox */}
          <div className="flex items-center gap-2 mb-1">
            <button onClick={() => setAgree(!agree)}
              className={`w-6 h-6 rounded-full flex items-center justify-center ${
                agree ? "bg-rose-500 text-white" : "bg-slate-200 text-transparent"
              }`}
              data-testid="bet-agree"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className="text-slate-700 text-sm">I agree</span>
            <span className="text-rose-500 text-sm font-bold">《Pre-sale rules》</span>
          </div>

          {/* Insufficient balance warning */}
          {total > Number(balance || 0) && (
            <div className="mt-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-600 font-semibold flex items-center gap-2" data-testid="bet-insufficient">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16.01l.01-.011" strokeLinecap="round"/></svg>
              Insufficient balance — please top up your wallet.
            </div>
          )}
        </div>

        {/* Bottom action bar */}
        <div className="flex">
          <button onClick={onClose}
            className="flex-1 py-4 bg-white text-slate-500 font-bold border-t"
            data-testid="bet-cancel"
          >Cancel</button>
          <button
            onClick={() => agree && total <= Number(balance || 0) && onConfirm({ amount: total, betType, betValue })}
            disabled={!agree || total > Number(balance || 0)}
            className={`flex-[2] py-4 font-bold text-white ${agree && total <= Number(balance || 0) ? "bg-[#ff9a4a]" : "bg-slate-300"}`}
            data-testid="bet-confirm"
          >
            Total amount ৳{total.toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Big result reveal overlay ─────────────────────────────
function ResultOverlay({ show, digits, game, extra }) {
  if (!show || !digits) return null;
  const isWingoStyle = game === "wingo" || game === "trxwin";
  const num = isWingoStyle ? digits[0] : null;
  const cols = num != null ? (NUM_COLORS[num] || []) : [];
  const colorLabel = cols.includes("violet") && cols.length === 2
    ? (cols[0] === "red" ? "Red" : "Green")
    : cols[0] === "red" ? "Red" : cols[0] === "green" ? "Green" : "Violet";
  const primary = cols[0] === "red" ? "#e11d48" : cols[0] === "green" ? "#16a34a" : "#7e22ce";
  const primaryDark = cols[0] === "red" ? "#9f1239" : cols[0] === "green" ? "#065f46" : "#4c1d95";
  const primaryLight = cols[0] === "red" ? "#fecdd3" : cols[0] === "green" ? "#a7f3d0" : "#e9d5ff";
  const colorPillCls = cols[0] === "red"
    ? "bg-gradient-to-b from-rose-400 to-rose-600"
    : cols[0] === "green"
    ? "bg-gradient-to-b from-emerald-400 to-emerald-600"
    : "bg-gradient-to-b from-violet-400 to-violet-600";
  const isBig = extra?.size === "big";

  return (
    <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center rounded-2xl overflow-hidden">
      {/* Deep dark backdrop with radial spotlight */}
      <div className="absolute inset-0"
           style={{ background: `radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.75) 100%)`, backdropFilter: 'blur(3px)' }} />

      {/* Confetti burst */}
      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full pointer-events-none animate-[confettiFade_2.4s_ease-out_forwards]">
        {Array.from({ length: 32 }).map((_, i) => {
          const a = (i / 32) * Math.PI * 2;
          const r = 180;
          const cx = 200 + Math.cos(a) * r;
          const cy = 200 + Math.sin(a) * r;
          const colors = ['#fbbf24', '#f87171', '#34d399', '#60a5fa', '#c084fc', '#fb923c'];
          const color = colors[i % colors.length];
          return (
            <rect
              key={i}
              x={cx - 3} y={cy - 8} width="6" height="16"
              fill={color}
              transform={`rotate(${(i * 47) % 360} ${cx} ${cy})`}
              style={{ animation: `confettiBurst 1.4s ${i * 20}ms cubic-bezier(.4,.9,.5,1) both`, transformOrigin: `${cx}px ${cy}px` }}
            />
          );
        })}
      </svg>

      {isWingoStyle ? (
        <div className="relative flex flex-col items-center gap-3 px-6 animate-[revealPop_.7s_cubic-bezier(.34,1.56,.64,1)]">
          {/* Rotating light rays */}
          <div className="absolute -top-4 w-72 h-72 pointer-events-none animate-[spin_10s_linear_infinite]"
               style={{ background: `conic-gradient(from 0deg, transparent 0deg, ${primaryLight}44 20deg, transparent 30deg, transparent 50deg, ${primaryLight}66 70deg, transparent 80deg, transparent 100deg, ${primaryLight}44 120deg, transparent 130deg, transparent 150deg, ${primaryLight}66 170deg, transparent 180deg, transparent 200deg, ${primaryLight}44 220deg, transparent 230deg, transparent 250deg, ${primaryLight}66 270deg, transparent 280deg, transparent 300deg, ${primaryLight}44 320deg, transparent 330deg)`, borderRadius: '50%', filter: 'blur(2px)' }} />

          {/* Outer glowing ring */}
          <div className="relative w-52 h-52 sm:w-56 sm:h-56 rounded-full flex items-center justify-center"
               style={{
                 background: `radial-gradient(circle at 30% 25%, ${primaryLight} 0%, #fff 20%, ${primaryLight} 45%, ${primary} 70%, ${primaryDark} 100%)`,
                 boxShadow: `0 0 60px ${primary}88, 0 0 120px ${primary}44, inset 0 -8px 20px ${primaryDark}88, inset 0 8px 20px rgba(255,255,255,0.4)`,
               }}>
            {/* Golden trim ring */}
            <div className="absolute inset-2 rounded-full pointer-events-none"
                 style={{
                   background: 'transparent',
                   border: '3px solid transparent',
                   backgroundImage: 'linear-gradient(#fff,#fff), linear-gradient(135deg, #ffd25a 0%, #ffb547 30%, #ffe08a 55%, #d97706 100%)',
                   backgroundOrigin: 'border-box',
                   backgroundClip: 'padding-box, border-box',
                 }} />

            {/* Inner white face */}
            <div className="absolute inset-6 rounded-full flex items-center justify-center overflow-hidden"
                 style={{
                   background: `radial-gradient(circle at 30% 25%, #ffffff 0%, ${primaryLight}55 60%, ${primaryLight} 100%)`,
                   boxShadow: `inset 0 4px 10px rgba(255,255,255,0.7), inset 0 -6px 12px ${primary}22`,
                 }}>
              {/* Number with 3D gradient */}
              <span
                className="relative font-black leading-none select-none animate-[numberPop_.9s_cubic-bezier(.34,1.56,.64,1)_.15s_backwards]"
                style={{
                  fontFamily: "'Bebas Neue', Impact, 'Arial Black', sans-serif",
                  fontSize: '150px',
                  background: `linear-gradient(180deg, ${primaryLight} 0%, ${primary} 40%, ${primaryDark} 100%)`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  WebkitTextStroke: `2px ${primaryDark}`,
                  textShadow: `0 6px 12px ${primaryDark}55, 0 2px 0 ${primaryDark}`,
                  filter: `drop-shadow(0 4px 6px ${primary}66)`,
                }}
                data-testid="result-number"
              >
                {num}
              </span>
            </div>

            {/* Corner sparkles */}
            {[[-10, -10, 40, 60], [58, -14, 32, 90], [-14, 55, 30, 30], [55, 58, 36, 120]].map(([tx, ty, size, delay], i) => (
              <svg key={i} viewBox="0 0 24 24" className="absolute text-amber-300 drop-shadow-lg"
                   style={{ top: `${ty}%`, left: `${tx}%`, width: `${size}px`, height: `${size}px`,
                            animation: `sparklePop 1.4s ${delay}ms ease-out infinite` }}
                   fill="currentColor">
                <path d="M12 2l1.7 6.3L20 10l-6.3 1.7L12 18l-1.7-6.3L4 10l6.3-1.7z" />
              </svg>
            ))}

            {/* Top glossy highlight */}
            <span className="absolute rounded-full pointer-events-none" style={{
              top:'10%', left:'22%', width:'55%', height:'22%',
              background:'radial-gradient(ellipse at center, rgba(255,255,255,0.85) 0%, transparent 65%)',
              filter:'blur(3px)'
            }}/>
          </div>

          {/* Result info banner */}
          <div className="flex items-center gap-2 mt-2 px-4 py-2 rounded-full backdrop-blur-md animate-[revealPop_.6s_.25s_cubic-bezier(.34,1.56,.64,1)_backwards]"
               style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.25)' }}>
            <span className={`inline-flex items-center px-3 py-1 rounded-full ${colorPillCls} text-white text-xs font-black shadow-lg ring-1 ring-white/40 tracking-wide`}>
              {colorLabel}
            </span>
            <span className="text-white/90 text-lg font-black">·</span>
            {extra?.size && (
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-white text-xs font-black shadow-lg ring-1 ring-white/40 tracking-wide ${
                isBig ? "bg-gradient-to-b from-amber-400 to-orange-500" : "bg-gradient-to-b from-sky-400 to-blue-500"
              }`}>
                {isBig ? "Big" : "Small"}
              </span>
            )}
          </div>
        </div>
      ) : (
        (() => {
          const count = digits.length;
          const many = count >= 4; // 5D
          const cardCls = many
            ? "w-[54px] h-[68px] sm:w-20 sm:h-24 rounded-xl sm:rounded-2xl"
            : "w-20 h-24 sm:w-24 sm:h-28 rounded-2xl";
          const textCls = many
            ? "font-black text-[38px] sm:text-6xl drop-shadow-md leading-none"
            : "font-black text-6xl sm:text-7xl drop-shadow-md";
          const gapCls = many ? "gap-1.5 sm:gap-3 px-2" : "gap-3 px-4";
          return (
            <div className={`relative animate-[revealPop_.7s_cubic-bezier(.34,1.56,.64,1)] flex items-center ${gapCls}`}>
              {digits.map((d, i) => (
                <div key={i}
                  className={`${cardCls} bg-white shadow-2xl ring-2 sm:ring-4 ring-amber-300 flex items-center justify-center`}
                  style={{ animation: `numberPop .7s ${i * 100}ms cubic-bezier(.34,1.56,.64,1) backwards` }}
                >
                  <span className={textCls}
                        style={{
                          background: 'linear-gradient(180deg, #fca5a5 0%, #e11d48 50%, #9f1239 100%)',
                          WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
                          WebkitTextStroke: many ? '1.4px #9f1239' : '2px #9f1239',
                        }}>{d}</span>
                </div>
              ))}
            </div>
          );
        })()
      )}
    </div>
  );
}

// ─── Last-5-seconds countdown overlay (giant flip cards over dimmed panel) ─────
function CountdownOverlay({ seconds }) {
  // seconds: 1..5 (only shown when in this range)
  if (seconds == null || seconds <= 0 || seconds > 10) return null;
  const t = String(seconds).padStart(2, "0");
  const d1 = t[0];
  const d2 = t[1];
  return (
    <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center gap-3 sm:gap-4 rounded-2xl overflow-hidden"
         data-testid="countdown-overlay">
      {/* Dim the panel underneath */}
      <div className="absolute inset-0 bg-slate-500/45 backdrop-blur-[1px]" />
      {[d1, d2].map((d, i) => (
        <div
          key={`${seconds}-${i}`}
          className="relative w-24 h-32 sm:w-28 sm:h-36 rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 flex items-center justify-center animate-[flipIn_.4s_cubic-bezier(.34,1.56,.64,1)]"
          style={{ transformOrigin: "center" }}
        >
          <span className="font-black text-[86px] sm:text-[100px] leading-none text-rose-500 drop-shadow-sm" style={{ fontFamily: "Arial Rounded MT Bold, Arial, sans-serif" }}>
            {d}
          </span>
          {/* Middle horizontal card seam */}
          <span className="absolute left-2 right-2 top-1/2 -translate-y-1/2 h-px bg-slate-200/80 pointer-events-none" />
        </div>
      ))}
    </div>
  );
}

// ─── Congratulations WIN popup ─────────────────────────────
function WinPopup({ data, onClose }) {
  const [cd, setCd] = useState(3);
  useEffect(() => {
    if (!data) return;
    setCd(3);
    const t = setInterval(() => {
      setCd((v) => {
        if (v <= 1) { clearInterval(t); onClose(); return 0; }
        return v - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [data, onClose]);
  if (!data) return null;
  const r = data.result || {};
  // Build the 3-pill result row per game — all pink toned like Hignice reference
  const pinkPill = "bg-gradient-to-b from-[#ff8698] to-[#ff5a70]";
  const softPill = "bg-white/20 ring-1 ring-white/40";
  let pills = [];
  if (data.game === "wingo" || data.game === "trxwin") {
    const cols = NUM_COLORS[r.number] || [];
    const colorLabel = cols.includes("violet") && cols.length === 2
      ? (cols[0] === "red" ? "Red" : "Green")
      : cols[0] === "red" ? "Red" : cols[0] === "green" ? "Green" : "Violet";
    pills = [
      { label: colorLabel, cls: pinkPill },
      { label: String(r.number ?? "?"), cls: softPill },
      { label: r.size === "big" ? "Big" : "Small", cls: pinkPill },
    ];
  } else if (data.game === "k3") {
    pills = [
      { label: (r.dice || []).join(""), cls: pinkPill },
      { label: `${r.sum ?? "?"}`, cls: softPill },
      { label: (r.size || "").toUpperCase(), cls: pinkPill },
    ];
  } else if (data.game === "5d") {
    pills = [
      { label: (r.digits || []).join(""), cls: pinkPill },
      { label: `${r.sum ?? "?"}`, cls: softPill },
      { label: (r.sum_size || "").toUpperCase(), cls: pinkPill },
    ];
  }
  const periodDisplay = formatPeriod(
    { start_at: new Date().toISOString(), period: data.period, duration: data.duration || 60 },
    data.game
  );
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
      data-testid="win-popup"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm animate-[popup_.5s_cubic-bezier(.34,1.56,.64,1)]"
      >
        {/* ─── Top ornament: WINGS + RIBBONS + COIN (single big SVG) ─── */}
        <div className="relative -mb-14 z-20 pointer-events-none">
          <svg viewBox="0 0 500 320" className="block w-full h-auto" preserveAspectRatio="xMidYMax meet">
            <defs>
              <radialGradient id="wpCoinRim" cx="35%" cy="30%" r="70%">
                <stop offset="0%" stopColor="#fff2b0"/>
                <stop offset="35%" stopColor="#ffd25a"/>
                <stop offset="75%" stopColor="#ffa027"/>
                <stop offset="100%" stopColor="#c96b0a"/>
              </radialGradient>
              <radialGradient id="wpCoinFace" cx="35%" cy="30%" r="70%">
                <stop offset="0%" stopColor="#ffde7a"/>
                <stop offset="55%" stopColor="#ffb547"/>
                <stop offset="100%" stopColor="#ef7a1a"/>
              </radialGradient>
              <linearGradient id="wpRibbonMain" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffb37a"/>
                <stop offset="50%" stopColor="#ff8a4a"/>
                <stop offset="100%" stopColor="#ee6633"/>
              </linearGradient>
              <linearGradient id="wpRibbonEdge" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#d94a1e"/>
                <stop offset="100%" stopColor="#c94020"/>
              </linearGradient>
              <linearGradient id="wpRibbonFold" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#c94422"/>
                <stop offset="100%" stopColor="#ff7a4a"/>
              </linearGradient>
              <linearGradient id="wpWingGrad" x1="0" y1="0" x2="0.6" y2="1">
                <stop offset="0%" stopColor="#ffffff"/>
                <stop offset="60%" stopColor="#ffefdd"/>
                <stop offset="100%" stopColor="#ffceac"/>
              </linearGradient>
              <filter id="wpDropShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#000" floodOpacity="0.25"/>
              </filter>
            </defs>

            {/* ═══ LEFT RIBBON (behind everything) ═══ */}
            {/* Big banner hanging DOWN from behind coin */}
            {/* Upper "come out" section: from behind coin, curl outward */}
            <path d="M 200 105
                     C 170 90, 130 88, 90 100
                     C 55 110, 30 130, 25 155
                     L 60 175
                     C 75 155, 105 145, 140 148
                     C 175 152, 200 155, 220 155
                     Z" fill="url(#wpRibbonMain)"/>
            {/* Fold at coin edge (darker) */}
            <path d="M 220 155 L 200 105 L 235 118 L 240 165 Z" fill="url(#wpRibbonFold)"/>

            {/* Hanging vertical banner tail */}
            <path d="M 25 155
                     L 60 175
                     L 60 265
                     C 60 275, 55 285, 45 290
                     C 30 296, 15 288, 15 270
                     L 15 175
                     Z" fill="url(#wpRibbonMain)"/>
            {/* V-cut fishtail at bottom */}
            <path d="M 60 265
                     L 40 290
                     L 30 275
                     L 25 300
                     L 60 305
                     Z" fill="url(#wpRibbonEdge)" opacity="0.9"/>
            <path d="M 15 175 L 25 155 L 30 175 Z" fill="url(#wpRibbonEdge)" opacity="0.5"/>

            {/* ═══ RIGHT RIBBON (mirror) ═══ */}
            <path d="M 300 105
                     C 330 90, 370 88, 410 100
                     C 445 110, 470 130, 475 155
                     L 440 175
                     C 425 155, 395 145, 360 148
                     C 325 152, 300 155, 280 155
                     Z" fill="url(#wpRibbonMain)"/>
            <path d="M 280 155 L 300 105 L 265 118 L 260 165 Z" fill="url(#wpRibbonFold)"/>
            <path d="M 475 155
                     L 440 175
                     L 440 265
                     C 440 275, 445 285, 455 290
                     C 470 296, 485 288, 485 270
                     L 485 175
                     Z" fill="url(#wpRibbonMain)"/>
            <path d="M 440 265
                     L 460 290
                     L 470 275
                     L 475 300
                     L 440 305
                     Z" fill="url(#wpRibbonEdge)" opacity="0.9"/>
            <path d="M 485 175 L 475 155 L 470 175 Z" fill="url(#wpRibbonEdge)" opacity="0.5"/>

            {/* ═══ WINGS (behind coin) ═══ */}
            <g filter="url(#wpDropShadow)">
              {/* Left wing — layered feathers */}
              <path d="M 220 95
                       C 195 65, 145 55, 90 68
                       C 65 74, 45 82, 30 95
                       C 55 100, 90 100, 130 102
                       C 165 105, 195 108, 220 112
                       Z" fill="url(#wpWingGrad)"/>
              <path d="M 220 105
                       C 195 90, 155 82, 105 90
                       C 80 95, 60 102, 45 112
                       C 75 115, 110 116, 145 118
                       C 175 119, 200 120, 220 122
                       Z" fill="#ffe0c9"/>
              <path d="M 220 115
                       C 195 108, 165 105, 130 110
                       C 105 115, 85 122, 70 130
                       C 100 130, 130 130, 160 130
                       C 185 131, 205 132, 220 132
                       Z" fill="#ffd0b3"/>
              {/* Right wing — mirror */}
              <path d="M 280 95
                       C 305 65, 355 55, 410 68
                       C 435 74, 455 82, 470 95
                       C 445 100, 410 100, 370 102
                       C 335 105, 305 108, 280 112
                       Z" fill="url(#wpWingGrad)"/>
              <path d="M 280 105
                       C 305 90, 345 82, 395 90
                       C 420 95, 440 102, 455 112
                       C 425 115, 390 116, 355 118
                       C 325 119, 300 120, 280 122
                       Z" fill="#ffe0c9"/>
              <path d="M 280 115
                       C 305 108, 335 105, 370 110
                       C 395 115, 415 122, 430 130
                       C 400 130, 370 130, 340 130
                       C 315 131, 295 132, 280 132
                       Z" fill="#ffd0b3"/>
            </g>

            {/* ═══ COIN (top layer) ═══ */}
            <g filter="url(#wpDropShadow)">
              <circle cx="250" cy="105" r="78" fill="url(#wpCoinRim)"/>
              <circle cx="250" cy="105" r="65" fill="url(#wpCoinFace)"/>
              {/* Rim shine */}
              <ellipse cx="220" cy="70" rx="35" ry="12" fill="#fff" opacity="0.4"/>
              <ellipse cx="223" cy="72" rx="20" ry="7" fill="#fff" opacity="0.7"/>
              {/* Rocket icon */}
              <g transform="translate(215,66) scale(1.1)" fill="#ffffff">
                <path d="M32 6 C 42 12, 46 22, 46 32 L 46 44 L 50 52 L 14 52 L 18 44 L 18 32 C 18 22, 22 12, 32 6 Z"/>
                <circle cx="32" cy="26" r="4.5" fill="#ff8a20"/>
                <path d="M18 44 L 10 56 L 22 56 Z M46 44 L 54 56 L 42 56 Z" opacity="0.9"/>
                <path d="M28 52 L 32 60 L 36 52 Z" opacity="0.75"/>
              </g>
            </g>

          </svg>
        </div>

        {/* ─── Card body with two small semicircle notches at top corners ─── */}
        <div className="relative rounded-[28px] px-6 pt-14 pb-6 shadow-2xl"
             style={{
               background:'linear-gradient(180deg, #ff9a5b 0%, #ff7259 45%, #ff5a5a 100%)',
               boxShadow:'0 20px 40px rgba(224,71,71,0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
             }}>
          <div className="text-center">
            <div className="text-white font-black text-[34px] italic tracking-wide drop-shadow-md" style={{fontFamily:'Georgia,serif'}}>
              Congratulations
            </div>
          </div>

          {/* Result pills */}
          <div className="mt-5 flex items-center gap-2">
            <span className="text-white font-bold text-sm">Lottery results</span>
            <div className="flex items-center gap-1.5 ml-auto">
              {pills.map((p, i) => (
                <span key={i}
                  className={`inline-flex items-center px-3.5 py-1 rounded-full ${p.cls} text-white text-xs font-bold`}
                  data-testid={`win-pill-${i}`}
                >
                  {p.label}
                </span>
              ))}
            </div>
          </div>

          {/* Bonus ticket — banner-shape SVG */}
          <div className="mt-4 relative h-[135px]">
            <svg viewBox="0 0 380 140" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
              <defs>
                <filter id="ticketShadow" x="-10%" y="-10%" width="120%" height="140%">
                  <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#000" floodOpacity="0.18"/>
                </filter>
              </defs>
              {/* Banner shape: rectangle with two semicircle cut-in on top corners, gentle curve on sides, wavy bottom ends */}
              <path d="
                M 30 8
                Q 40 0 60 6
                Q 100 20 190 15
                Q 280 20 320 6
                Q 340 0 350 8
                L 358 20
                Q 372 60 358 105
                L 350 118
                Q 340 130 322 122
                Q 300 108 275 122
                Q 240 138 190 128
                Q 140 138 105 122
                Q 80 108 58 122
                Q 40 130 30 118
                L 22 105
                Q 8 60 22 20
                Z" fill="#ffffff" filter="url(#ticketShadow)"/>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 pt-2">
              <div className="text-slate-700 text-sm font-bold">Bonus</div>
              <div className="text-rose-500 font-black text-[30px] leading-tight mt-0.5" data-testid="win-payout">
                ৳{Number(data.payout || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-slate-500 text-xs mt-1">Period: {data.gameLabel}</div>
              <div className="text-slate-500 text-[12px] font-mono tracking-wider">
                {periodDisplay}
              </div>
            </div>
          </div>

          {/* Countdown */}
          <div className="mt-4 flex items-center justify-center gap-2 text-white text-sm">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full ring-2 ring-white/70">
              <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="4">
                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span data-testid="win-countdown">{cd} seconds auto close</span>
          </div>
        </div>

        {/* Close */}
        <button onClick={onClose} data-testid="win-close"
          className="mx-auto mt-4 flex items-center justify-center w-12 h-12 rounded-full bg-white/95 hover:bg-white text-slate-400 text-2xl shadow-lg">
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10"/>
            <path d="M8 8l8 8M16 8l-8 8" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── LOSS popup — same structure, cool gray/blue theme ─────────────────
function LossPopup({ data, onClose }) {
  const [cd, setCd] = useState(3);
  useEffect(() => {
    if (!data) return;
    setCd(3);
    const t = setInterval(() => {
      setCd((v) => {
        if (v <= 1) { clearInterval(t); onClose(); return 0; }
        return v - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [data, onClose]);
  if (!data) return null;
  const r = data.result || {};
  const grayPill = "bg-gradient-to-b from-slate-400 to-slate-500";
  const softPill = "bg-white/20 ring-1 ring-white/40";
  let pills = [];
  if (data.game === "wingo" || data.game === "trxwin") {
    const cols = NUM_COLORS[r.number] || [];
    const colorLabel = cols.includes("violet") && cols.length === 2
      ? (cols[0] === "red" ? "Red" : "Green")
      : cols[0] === "red" ? "Red" : cols[0] === "green" ? "Green" : "Violet";
    pills = [
      { label: colorLabel, cls: grayPill },
      { label: String(r.number ?? "?"), cls: softPill },
      { label: r.size === "big" ? "Big" : "Small", cls: grayPill },
    ];
  } else if (data.game === "k3") {
    pills = [
      { label: (r.dice || []).join(""), cls: grayPill },
      { label: `${r.sum ?? "?"}`, cls: softPill },
      { label: (r.size || "").toUpperCase(), cls: grayPill },
    ];
  } else if (data.game === "5d") {
    pills = [
      { label: (r.digits || []).join(""), cls: grayPill },
      { label: `${r.sum ?? "?"}`, cls: softPill },
      { label: (r.sum_size || "").toUpperCase(), cls: grayPill },
    ];
  }
  const periodDisplay = formatPeriod(
    { start_at: new Date().toISOString(), period: data.period, duration: data.duration || 60 },
    data.game
  );
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
      data-testid="loss-popup"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm animate-[popup_.5s_cubic-bezier(.34,1.56,.64,1)]"
      >
        {/* ─── Top ornament: WINGS + RIBBONS + COIN (silver theme) ─── */}
        <div className="relative -mb-14 z-20 pointer-events-none">
          <svg viewBox="0 0 500 320" className="block w-full h-auto" preserveAspectRatio="xMidYMax meet">
            <defs>
              <radialGradient id="lpCoinRim" cx="35%" cy="30%" r="70%">
                <stop offset="0%" stopColor="#f8fafc"/>
                <stop offset="35%" stopColor="#cbd5e1"/>
                <stop offset="75%" stopColor="#94a3b8"/>
                <stop offset="100%" stopColor="#475569"/>
              </radialGradient>
              <radialGradient id="lpCoinFace" cx="35%" cy="30%" r="70%">
                <stop offset="0%" stopColor="#e2e8f0"/>
                <stop offset="55%" stopColor="#94a3b8"/>
                <stop offset="100%" stopColor="#64748b"/>
              </radialGradient>
              <linearGradient id="lpRibbonMain" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#94a3b8"/>
                <stop offset="50%" stopColor="#64748b"/>
                <stop offset="100%" stopColor="#475569"/>
              </linearGradient>
              <linearGradient id="lpRibbonEdge" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#334155"/>
                <stop offset="100%" stopColor="#1e293b"/>
              </linearGradient>
              <linearGradient id="lpRibbonFold" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#334155"/>
                <stop offset="100%" stopColor="#64748b"/>
              </linearGradient>
              <linearGradient id="lpWingGrad" x1="0" y1="0" x2="0.6" y2="1">
                <stop offset="0%" stopColor="#ffffff"/>
                <stop offset="60%" stopColor="#f1f5f9"/>
                <stop offset="100%" stopColor="#cbd5e1"/>
              </linearGradient>
              <filter id="lpDropShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#000" floodOpacity="0.25"/>
              </filter>
            </defs>

            {/* LEFT RIBBON */}
            <path d="M 200 105 C 170 90, 130 88, 90 100 C 55 110, 30 130, 25 155 L 60 175 C 75 155, 105 145, 140 148 C 175 152, 200 155, 220 155 Z" fill="url(#lpRibbonMain)"/>
            <path d="M 220 155 L 200 105 L 235 118 L 240 165 Z" fill="url(#lpRibbonFold)"/>
            <path d="M 25 155 L 60 175 L 60 265 C 60 275, 55 285, 45 290 C 30 296, 15 288, 15 270 L 15 175 Z" fill="url(#lpRibbonMain)"/>
            <path d="M 60 265 L 40 290 L 30 275 L 25 300 L 60 305 Z" fill="url(#lpRibbonEdge)" opacity="0.9"/>
            <path d="M 15 175 L 25 155 L 30 175 Z" fill="url(#lpRibbonEdge)" opacity="0.5"/>

            {/* RIGHT RIBBON */}
            <path d="M 300 105 C 330 90, 370 88, 410 100 C 445 110, 470 130, 475 155 L 440 175 C 425 155, 395 145, 360 148 C 325 152, 300 155, 280 155 Z" fill="url(#lpRibbonMain)"/>
            <path d="M 280 155 L 300 105 L 265 118 L 260 165 Z" fill="url(#lpRibbonFold)"/>
            <path d="M 475 155 L 440 175 L 440 265 C 440 275, 445 285, 455 290 C 470 296, 485 288, 485 270 L 485 175 Z" fill="url(#lpRibbonMain)"/>
            <path d="M 440 265 L 460 290 L 470 275 L 475 300 L 440 305 Z" fill="url(#lpRibbonEdge)" opacity="0.9"/>
            <path d="M 485 175 L 475 155 L 470 175 Z" fill="url(#lpRibbonEdge)" opacity="0.5"/>

            {/* WINGS */}
            <g filter="url(#lpDropShadow)">
              <path d="M 220 95 C 195 65, 145 55, 90 68 C 65 74, 45 82, 30 95 C 55 100, 90 100, 130 102 C 165 105, 195 108, 220 112 Z" fill="url(#lpWingGrad)"/>
              <path d="M 220 105 C 195 90, 155 82, 105 90 C 80 95, 60 102, 45 112 C 75 115, 110 116, 145 118 C 175 119, 200 120, 220 122 Z" fill="#e2e8f0"/>
              <path d="M 220 115 C 195 108, 165 105, 130 110 C 105 115, 85 122, 70 130 C 100 130, 130 130, 160 130 C 185 131, 205 132, 220 132 Z" fill="#cbd5e1"/>
              <path d="M 280 95 C 305 65, 355 55, 410 68 C 435 74, 455 82, 470 95 C 445 100, 410 100, 370 102 C 335 105, 305 108, 280 112 Z" fill="url(#lpWingGrad)"/>
              <path d="M 280 105 C 305 90, 345 82, 395 90 C 420 95, 440 102, 455 112 C 425 115, 390 116, 355 118 C 325 119, 300 120, 280 122 Z" fill="#e2e8f0"/>
              <path d="M 280 115 C 305 108, 335 105, 370 110 C 395 115, 415 122, 430 130 C 400 130, 370 130, 340 130 C 315 131, 295 132, 280 132 Z" fill="#cbd5e1"/>
            </g>

            {/* COIN */}
            <g filter="url(#lpDropShadow)">
              <circle cx="250" cy="105" r="78" fill="url(#lpCoinRim)"/>
              <circle cx="250" cy="105" r="65" fill="url(#lpCoinFace)"/>
              <ellipse cx="220" cy="70" rx="35" ry="12" fill="#fff" opacity="0.4"/>
              <ellipse cx="223" cy="72" rx="20" ry="7" fill="#fff" opacity="0.7"/>
              {/* Sad face */}
              <g transform="translate(215,66) scale(1.1)" fill="#ffffff">
                <circle cx="22" cy="26" r="4.2"/>
                <circle cx="42" cy="26" r="4.2"/>
                <path d="M 16 46 Q 32 34 48 46" stroke="#ffffff" strokeWidth="4" fill="none" strokeLinecap="round"/>
              </g>
            </g>
          </svg>
        </div>

        {/* Card body */}
        <div className="relative rounded-[28px] px-6 pt-14 pb-6 shadow-2xl"
             style={{
               background:'linear-gradient(180deg, #94a3b8 0%, #64748b 45%, #475569 100%)',
               boxShadow:'0 20px 40px rgba(71,85,105,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
             }}>
          <div className="text-center">
            <div className="text-white font-black text-[32px] italic tracking-wide drop-shadow-md" style={{fontFamily:'Georgia,serif'}}>
              Sorry, no win
            </div>
          </div>

          <div className="mt-5 flex items-center gap-2">
            <span className="text-white font-bold text-sm">Lottery results</span>
            <div className="flex items-center gap-1.5 ml-auto">
              {pills.map((p, i) => (
                <span key={i}
                  className={`inline-flex items-center px-3.5 py-1 rounded-full ${p.cls} text-white text-xs font-bold`}
                  data-testid={`loss-pill-${i}`}
                >
                  {p.label}
                </span>
              ))}
            </div>
          </div>

          {/* Banner-shape ticket */}
          <div className="mt-4 relative h-[135px]">
            <svg viewBox="0 0 380 140" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
              <defs>
                <filter id="lpTicketShadow" x="-10%" y="-10%" width="120%" height="140%">
                  <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#000" floodOpacity="0.18"/>
                </filter>
              </defs>
              <path d="
                M 30 8
                Q 40 0 60 6
                Q 100 20 190 15
                Q 280 20 320 6
                Q 340 0 350 8
                L 358 20
                Q 372 60 358 105
                L 350 118
                Q 340 130 322 122
                Q 300 108 275 122
                Q 240 138 190 128
                Q 140 138 105 122
                Q 80 108 58 122
                Q 40 130 30 118
                L 22 105
                Q 8 60 22 20
                Z" fill="#ffffff" filter="url(#lpTicketShadow)"/>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 pt-2">
              <div className="text-slate-700 text-sm font-bold">Lucky next time</div>
              <div className="text-slate-600 font-black text-[30px] leading-tight mt-0.5" data-testid="loss-amount">
                −৳{Number(data.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-slate-500 text-xs mt-1">Period: {data.gameLabel}</div>
              <div className="text-slate-500 text-[12px] font-mono tracking-wider">
                {periodDisplay}
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-center gap-2 text-white text-sm">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full ring-2 ring-white/70">
              <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="4">
                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span data-testid="loss-countdown">{cd} seconds auto close</span>
          </div>
        </div>

        <button onClick={onClose} data-testid="loss-close"
          className="mx-auto mt-4 flex items-center justify-center w-12 h-12 rounded-full bg-white/95 hover:bg-white text-slate-400 text-2xl shadow-lg">
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10"/>
            <path d="M8 8l8 8M16 8l-8 8" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════
export default function LotteryGamePage() {
  const { game } = useParams();
  const navigate = useNavigate();
  const { user, checking, refreshUser } = useAuth();

  // Auth gate — lottery games require a logged-in user. If AuthContext has
  // finished checking and there is still no user, kick them to /login and
  // remember the page they were trying to reach so they land back here.
  useEffect(() => {
    if (!checking && !user) {
      navigate("/login", {
        replace: true,
        state: { from: { pathname: `/lottery/${game || ""}` } },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checking, user, game]);

  const durations = DURATIONS[game] || DURATIONS.wingo;
  const [duration, setDuration] = useState(durations[0].s);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [myBets, setMyBets] = useState([]);
  const [config, setConfig] = useState(null);
  const [tab, setTab] = useState("history");
  const [dialog, setDialog] = useState(null);
  const [showReveal, setShowReveal] = useState(false);
  const [revealDigits, setRevealDigits] = useState(null);
  const [revealExtra, setRevealExtra] = useState(null);
  const [multChip, setMultChip] = useState(1);
  const [posSel, setPosSel] = useState("A"); // for 5D
  const [winPopup, setWinPopup] = useState(null); // { payout, result, period, gameLabel }
  const [lossPopup, setLossPopup] = useState(null); // { amount, result, period, gameLabel, game }
  const [showHowTo, setShowHowTo] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [betsPage, setBetsPage] = useState(1);
  const PAGE_SIZE = 12;
  const MAX_ITEMS = 50;
  const lastPeriodRef = useRef(null);
  const seenBetIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  const lastTickRef = useRef(null);

  useEffect(() => {
    // If landing game url is invalid, redirect
    if (!GAME_TITLES[game]) { navigate("/lottery"); }
  }, [game, navigate]);

  // Fetch config once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/lottery/games");
        setConfig(data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // Poll current round every 1s
  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const { data } = await api.get("/lottery/current", { params: { game, duration } });
        if (cancelled) return;
        // On period change → refetch history/bets, refresh balance, trigger reveal
        if (lastPeriodRef.current && lastPeriodRef.current !== data.round.period) {
          const prev = lastPeriodRef.current;
          setTimeout(async () => {
            try {
              const h = await api.get("/lottery/history", { params: { game, duration, limit: 50 } });
              const last = (h.data.items || []).find((x) => x.period === prev);
              if (last?.result) {
                let dg = [];
                if (game === "wingo" || game === "trxwin") dg = [last.result.number ?? "?"];
                else if (game === "k3") dg = last.result.dice || [];
                else if (game === "5d") dg = last.result.digits || [];
                if (dg.length) {
                  setRevealDigits(dg);
                  setRevealExtra({ size: last.result.size, parity: last.result.parity, sum: last.result.sum });
                  setShowReveal(true);
                  setTimeout(() => setShowReveal(false), 3200);
                }
              }
              setHistory(h.data.items || []);
            } catch { /* ignore */ }
          }, 400);
          fetchMyBets();
          refreshUser?.();
        }
        lastPeriodRef.current = data.round.period;
        setCurrent(data);
      } catch (e) { /* ignore */ }
    }
    poll();
    const t = setInterval(poll, 1000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, duration]);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/lottery/history", { params: { game, duration, limit: 50 } });
      setHistory(data.items || []);
    } catch { /* ignore */ }
  }, [game, duration]);

  const fetchMyBets = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/lottery/my-bets", { params: { game, limit: 50 } });
      setMyBets(data.items || []);
    } catch { /* ignore */ }
  }, [game, user]);

  useEffect(() => { fetchHistory(); fetchMyBets(); setHistoryPage(1); setBetsPage(1); }, [fetchHistory, fetchMyBets]);

  // Demo popups via URL query param (?demo=win / ?demo=loss) — for reference/screenshot
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const dur = durations[0].s;
    const demoData = {
      result: { number: 8, size: "big", dice: [3, 5, 6], sum: 14, digits: [1,2,3,4,5], sum_size: "big" },
      period: `20260705${dur === 30 ? "30" : "01"}0010928`,
      gameLabel: `${GAME_TITLES[game] || "Game"} ${durations[0].label}`,
      game,
      duration: dur,
    };
    if (q.get("demo") === "win") setWinPopup({ ...demoData, payout: 3136 });
    else if (q.get("demo") === "loss") setLossPopup({ ...demoData, amount: 100 });
    else if (q.get("demo") === "reveal") {
      setRevealDigits([3]);
      setRevealExtra({ size: "small" });
      setShowReveal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game]);

  // Independent poll of my-bets so we catch wins even when the visible
  // duration's period hasn't ticked yet (e.g., user placed a longer-duration
  // bet in a different tab, or admin force-settled).
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => { fetchMyBets(); }, 2500);
    return () => clearInterval(t);
  }, [user, fetchMyBets]);

  // Detect newly-settled winning bets → show congrats popup
  useEffect(() => {
    if (!myBets || myBets.length === 0) return;
    if (firstLoadRef.current) {
      // On the first load, mark all currently-seen bets so we only pop up
      // for wins that happen AFTER the user opens the page.
      myBets.forEach((b) => seenBetIdsRef.current.add(b.id + ":" + b.status));
      firstLoadRef.current = false;
      return;
    }
    for (const b of myBets) {
      const key = b.id + ":" + b.status;
      if (seenBetIdsRef.current.has(key)) continue;
      seenBetIdsRef.current.add(key);
      if (b.status === "won" && b.payout > 0) {
        const dur = (DURATIONS[b.game] || []).find((x) => x.s === b.duration)?.label || `${b.duration}s`;
        sfxWin();
        setWinPopup({
          payout: b.payout,
          result: b.result || {},
          period: b.period,
          gameLabel: `${GAME_TITLES[b.game] || "Game"} ${dur}`,
          game: b.game,
          round_id: b.round_id,
          duration: b.duration,
        });
        break; // one at a time; older wins will pop next round
      }
      if (b.status === "lost") {
        const dur = (DURATIONS[b.game] || []).find((x) => x.s === b.duration)?.label || `${b.duration}s`;
        sfxLose();
        setLossPopup({
          amount: b.amount,
          result: b.result || {},
          period: b.period,
          gameLabel: `${GAME_TITLES[b.game] || "Game"} ${dur}`,
          game: b.game,
          round_id: b.round_id,
          duration: b.duration,
        });
        break;
      }
    }
  }, [myBets]);

  const mults = (config?.multipliers?.[game]) || {};

  async function confirmBet({ amount, betType, betValue }) {
    if (!user) { toast.error("Please login first"); navigate("/login"); return; }
    try {
      await api.post("/lottery/bet", {
        game, duration, bet_type: betType, bet_value: betValue, amount,
      });
      sfxBet();
      toast.success("Bet placed!");
      setDialog(null);
      refreshUser?.();
      fetchMyBets();
    } catch (e) {
      toast.error(extractApiError(e, "Failed to place bet"));
    }
  }

  const timeLeft = current?.time_left_seconds ?? 0;
  const canBet = current?.can_bet;
  const mm = Math.floor(timeLeft / 60), ss = timeLeft % 60;

  // Play tick sound each time we enter a new second in the last 5 seconds
  useEffect(() => {
    if (timeLeft > 0 && timeLeft <= 10) {
      if (lastTickRef.current !== timeLeft) {
        lastTickRef.current = timeLeft;
        sfxTick(timeLeft);
      }
    } else {
      lastTickRef.current = null;
    }
  }, [timeLeft]);

  const [m1, m2] = String(pad(mm)).split("");
  const [s1, s2] = String(pad(ss)).split("");
  const isTRX = game === "trxwin";
  const isWingoStyle = game === "wingo" || game === "trxwin";
  const meta = GAME_TITLES[game] || "Lottery";

  // Recent 5 results (mini balls)
  const recent5 = useMemo(() => history.slice(0, 5), [history]);

  // Compute multiplier from chip for bet amount
  function chipMult() {
    return multChip === "rand" ? 1 : (multChip || 1);
  }

  function openBet(type, value, label, m) {
    if (!canBet) { toast.error("Betting is locked"); return; }
    sfxTap();
    // Pre-fill amount using chip
    const baseAmt = 1 * chipMult(); // dialog default 10 * chipMult if user leaves as-is
    setDialog({ type, value, label, mult: m, presetChip: baseAmt });
  }

  return (
    <div className="min-h-[100vh] bg-[#fff5f6] pb-24">
      {/* Header — HIGNICE brand with logo icon */}
      <div className="bg-gradient-to-b from-[#ff8c9d] via-[#ff9aab] to-[#ffb6c3] px-4 pt-4 pb-8 relative">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/lottery")}
            data-testid="back-btn"
            aria-label="Back"
            className="group relative w-9 h-9 rounded-full bg-white flex items-center justify-center text-rose-500 shadow-[0_4px_10px_-2px_rgba(190,18,60,0.35),inset_0_-2px_4px_rgba(190,18,60,0.08)] transition-all duration-200 hover:text-rose-600 hover:shadow-[0_6px_14px_-2px_rgba(190,18,60,0.5)] active:scale-90"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 -ml-0.5">
              <polyline points="15 6 9 12 15 18" />
            </svg>
          </button>
          <div className="flex items-center gap-1.5">
            <span className="text-white font-black text-2xl italic tracking-tight drop-shadow-sm" style={{fontFamily:'Georgia,serif'}}>
              Maxlive<span className="text-white">88</span>
            </span>
          </div>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-3 -mt-5">
        {/* Duration tabs — active tab is a big red card sticking up */}
        <div className="relative bg-white rounded-2xl shadow p-1.5 mb-3">
          <div className="grid grid-cols-4 gap-1">
            {durations.map((d) => {
              const active = duration === d.s;
              return (
                <button key={d.s} onClick={() => setDuration(d.s)}
                  data-testid={`duration-${d.s}`}
                  className={`relative rounded-2xl px-1 pt-2 pb-2 flex flex-col items-center transition-all duration-200 ${
                    active
                      ? "text-white z-10 -mt-3 pt-4 pb-4 shadow-lg"
                      : "text-slate-500"
                  }`}
                  style={active ? { background: "linear-gradient(180deg, #ff4d67 0%, #ff8fa2 100%)" } : {}}
                >
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center ${active ? "bg-white/30 backdrop-blur" : "bg-slate-100"}`}
                    style={active ? { boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.55)" } : {}}
                  >
                    <svg viewBox="0 0 24 24" className={`w-6 h-6 ${active ? "text-white" : "text-slate-400"}`} fill="none" stroke="currentColor" strokeWidth="2.3">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3.5 2" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div className={`mt-1.5 text-[12px] font-bold leading-tight text-center ${active ? "text-white" : "text-slate-500"}`}>
                    {meta}<br/>{d.label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Period / Time — coral card with TOP + BOTTOM ticket notches */}
        <div className="relative mb-3">
          <div className={`relative rounded-3xl p-4 ${
            isTRX ? "bg-gradient-to-b from-[#ff9db0] to-[#ffc8d1]" : "bg-gradient-to-b from-[#ff5a76] to-[#ff8090]"
          }`}>
            {/* Top notch */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#fff5f6]" />
            {/* Bottom notch */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-6 h-6 rounded-full bg-[#fff5f6]" />
            {/* Dashed divider */}
            <div className="absolute top-6 bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center">
              <div className="flex-1 border-l-2 border-dashed border-white/70" />
            </div>

            <div className="grid grid-cols-2 gap-3 relative z-10">
              {/* Left side */}
              <div>
                <button
                  onClick={() => setShowHowTo(true)}
                  data-testid="howtoplay-btn"
                  className="inline-flex items-center gap-1 text-white text-[12px] font-bold px-3 py-1 rounded-full"
                  style={{ border: "1.5px solid rgba(255,255,255,0.7)" }}>
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor"><path d="M4 4h16v16l-4-3H4z"/></svg>
                  How to play
                </button>
                <div className="mt-2 text-white/95 font-bold text-sm drop-shadow-sm">
                  {meta} {durations.find((x) => x.s === duration)?.label}
                </div>
                {isTRX ? (
                  <div className="mt-2"><HashBubbles hash={history[0]?.result?.hash} /></div>
                ) : (
                  <div className="mt-2 flex gap-1.5" data-testid="recent-mini-balls">
                    {recent5.map((h) => (
                      <MiniBall key={h.id} n={h.result?.number} />
                    ))}
                  </div>
                )}
              </div>
              {/* Right side */}
              <div className="text-right">
                <div className="text-white text-sm font-bold drop-shadow-sm">Time remaining</div>
                <div className="mt-2 flex items-center justify-end" data-testid="time-remaining">
                  <TimerDigit d={m1} /><TimerDigit d={m2} />
                  <span className="text-white font-black mx-1 text-2xl drop-shadow">:</span>
                  <TimerDigit d={s1} /><TimerDigit d={s2} />
                </div>
                <div className="mt-1.5 text-white text-[13px] font-black tracking-wider" data-testid="current-period">
                  {formatPeriod(current?.round, game)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* GAME PANEL */}
        <div className="relative bg-white rounded-2xl shadow-sm p-3 mb-3">
          <ResultOverlay show={showReveal} digits={revealDigits} game={game} extra={revealExtra} />
          <CountdownOverlay seconds={timeLeft > 0 && timeLeft <= 10 ? timeLeft : 0} />

          {isWingoStyle ? (
            <WinGoPanel
              game={game}
              mults={mults}
              canBet={canBet}
              multChip={multChip}
              setMultChip={setMultChip}
              onBet={openBet}
            />
          ) : game === "k3" ? (
            <K3Panel mults={mults} canBet={canBet} onBet={openBet} lastDice={history[0]?.result?.dice} />
          ) : game === "5d" ? (
            <FiveDPanel mults={mults} canBet={canBet} onBet={openBet} posSel={posSel} setPosSel={setPosSel} />
          ) : null}
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 gap-2 mb-2">
          {[
            { k: "history", label: "Game history" },
            { k: "chart", label: "My Bet History" },
            { k: "mine", label: "Follow Strategy" },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`py-2.5 rounded-2xl font-bold text-sm ${
                tab === t.k ? "bg-gradient-to-b from-[#ff4d67] to-[#ff7a8c] text-white shadow" : "bg-white text-slate-500 shadow"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "history" && (() => {
          const capped = history.slice(0, MAX_ITEMS);
          const totalPages = Math.max(1, Math.ceil(capped.length / PAGE_SIZE));
          const p = Math.min(historyPage, totalPages);
          const pageItems = capped.slice((p - 1) * PAGE_SIZE, p * PAGE_SIZE);
          return (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {isTRX ? (
              <div className="grid grid-cols-5 gap-2 px-3 py-3 text-sm text-white bg-[#ff5a70] font-bold">
                <div>Period</div><div>Block h.</div><div>Block time</div><div>Hash</div><div className="text-right">Result</div>
              </div>
            ) : game === "k3" ? (
              <div
                className="grid gap-2 px-3 py-3 text-sm text-white bg-[#ff5a70] font-bold"
                style={{ gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr) minmax(0,0.5fr) minmax(0,1.1fr)" }}
              >
                <div>Period</div><div>Dice</div><div>Sum</div><div className="text-right">Big/Small</div>
              </div>
            ) : game === "5d" ? (
              <div
                className="grid gap-2 px-3 py-3 text-sm text-white bg-[#ff5a70] font-bold"
                style={{ gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1.3fr) minmax(0,0.5fr)" }}
              >
                <div>Period</div><div>Digits</div><div className="text-right">Sum</div>
              </div>
            ) : (
              <div
                className="grid gap-2 px-3 py-3 text-sm text-white bg-[#ff5a70] font-bold"
                style={{ gridTemplateColumns: "minmax(0,1.4fr) minmax(0,0.6fr) minmax(0,0.9fr) minmax(0,0.6fr)" }}
              >
                <div>Period</div><div className="text-center">Number</div><div className="text-center">Big Small</div><div className="text-right">Color</div>
              </div>
            )}
            {capped.length === 0 ? (
              <div className="text-slate-400 text-center py-6 text-sm">No history yet</div>
            ) : (
              pageItems.map((h) => (
                <HistoryRow key={h.id} h={h} game={game} isTRX={isTRX} />
              ))
            )}
            {capped.length > 0 && <Pagination page={p} totalPages={totalPages} onChange={setHistoryPage} testidPrefix="history" />}
          </div>
          );
        })()}

        {tab === "chart" && (() => {
          if (!user) {
            return <div className="bg-white rounded-2xl shadow-sm p-6 text-slate-400 text-center text-sm">Please login to see your bets</div>;
          }
          const capped = myBets.slice(0, MAX_ITEMS);
          const totalPages = Math.max(1, Math.ceil(capped.length / PAGE_SIZE));
          const p = Math.min(betsPage, totalPages);
          const pageItems = capped.slice((p - 1) * PAGE_SIZE, p * PAGE_SIZE);
          return (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="grid grid-cols-3 gap-2 px-3 py-3 text-sm text-white bg-[#ff5a70] font-bold">
              <div>Period</div><div className="text-center">Select</div><div className="text-right">Result</div>
            </div>
            {capped.length === 0 ? (
              <div className="text-slate-400 text-center py-6 text-sm">No bets yet</div>
            ) : (
              pageItems.map((b) => (
                <div key={b.id} className="grid grid-cols-3 gap-2 px-3 py-2.5 border-b border-slate-100 items-center text-sm" data-testid={`mybet-${b.id}`}>
                  <div>
                    <div className="font-mono text-[12px] text-slate-700 truncate">{String(b.period)}</div>
                    <div className="text-slate-400 text-[11px] mt-0.5">৳{Number(b.amount).toFixed(2)}</div>
                  </div>
                  <div className="text-center">
                    <span className="text-rose-500 font-bold text-xs">
                      {b.bet_type}: {typeof b.bet_value === "object" ? JSON.stringify(b.bet_value) : String(b.bet_value)}
                    </span>
                  </div>
                  <div className="text-right">
                    {b.status === "open" ? <span className="text-amber-500 font-bold text-sm">Pending</span>
                      : b.status === "won" ? <span className="text-emerald-500 font-bold text-sm">+৳{Number(b.payout).toFixed(2)}</span>
                      : <span className="text-rose-400 font-bold text-sm">Lost</span>}
                  </div>
                </div>
              ))
            )}
            {capped.length > 0 && <Pagination page={p} totalPages={totalPages} onChange={setBetsPage} testidPrefix="mybets" />}
          </div>
          );
        })()}

        {tab === "mine" && (
          <div className="bg-white rounded-2xl shadow-sm p-6 text-slate-400 text-center text-sm">
            Follow Strategy — coming soon.
          </div>
        )}
      </div>

      {/* Floating balance chip */}
      {user && (
        <div className="fixed bottom-3 right-3 z-30 bg-white rounded-full shadow-lg px-4 py-2 border border-rose-200">
          <span className="text-xs text-slate-500 mr-1">Bal</span>
          <span className="font-bold text-rose-500" data-testid="lottery-balance">৳{Number(user?.balance || 0).toFixed(2)}</span>
        </div>
      )}

      <BetDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        game={game}
        betType={dialog?.type}
        betValue={dialog?.value}
        label={dialog?.label}
        mult={dialog?.mult}
        balance={user?.balance}
        gameLabel={`${meta} ${durations.find((x) => x.s === duration)?.label || ""}`}
        onConfirm={confirmBet}
      />

      <WinPopup data={winPopup} onClose={() => setWinPopup(null)} />
      <LossPopup data={lossPopup} onClose={() => setLossPopup(null)} />

      <HowToPlayModal open={showHowTo} onClose={() => setShowHowTo(false)} game={game} />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Hind+Siliguri:wght@400;500;600;700&display=swap');
        @keyframes pop { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.1); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
        @keyframes popup { 0% { transform: scale(0.6) translateY(20px); opacity: 0; } 60% { transform: scale(1.05) translateY(-4px); opacity: 1; } 100% { transform: scale(1) translateY(0); opacity: 1; } }
        @keyframes bounceTap { 0% { transform: scale(1); } 30% { transform: scale(0.85); } 60% { transform: scale(1.08); } 100% { transform: scale(1); } }
        @keyframes flipIn { 0% { transform: rotateX(-90deg) scale(0.9); opacity: 0; } 60% { transform: rotateX(10deg) scale(1.04); opacity: 1; } 100% { transform: rotateX(0deg) scale(1); opacity: 1; } }
        @keyframes revealPop {
          0% { transform: scale(0.3) rotate(-15deg); opacity: 0; }
          40% { transform: scale(1.15) rotate(3deg); opacity: 1; }
          70% { transform: scale(0.95) rotate(-1deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes numberPop {
          0% { transform: scale(0) rotate(-30deg); opacity: 0; filter: blur(6px); }
          50% { transform: scale(1.3) rotate(5deg); opacity: 1; filter: blur(0); }
          75% { transform: scale(0.92) rotate(-2deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; filter: blur(0); }
        }
        @keyframes sparklePop {
          0%, 100% { transform: scale(0.6) rotate(0deg); opacity: 0.4; }
          50% { transform: scale(1.4) rotate(180deg); opacity: 1; }
        }
        @keyframes confettiBurst {
          0% { transform: translate(0,0) rotate(0deg) scale(0); opacity: 0; }
          15% { opacity: 1; }
          60% { transform: translate(0,0) rotate(180deg) scale(1); opacity: 1; }
          100% { transform: translate(var(--tx,0px), calc(var(--ty,80px))) rotate(720deg) scale(0.6); opacity: 0; }
        }
        @keyframes confettiFade {
          0%, 60% { opacity: 1; }
          100% { opacity: 0; }
        }
        .tap-bounce:active { animation: bounceTap 0.4s cubic-bezier(.34,1.56,.64,1); }
      `}</style>
    </div>
  );
}

// ═════════ WinGo / TRX ═════════
function WinGoPanel({ game, mults, canBet, multChip, setMultChip, onBet }) {
  const isMobile = useIsMobile();
  return (
    <div className="rounded-2xl p-3 relative bg-white">
      {/* Colours — solid pills with asymmetric corners like reference */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <button data-testid="wingo-green" onClick={() => onBet("color", "green", "Join Green", mults.green || 2)}
          className="py-3 sm:py-3.5 text-white font-semibold text-sm sm:text-base active:scale-95 transition"
          style={{
            background:'#22c55e',
            borderRadius:'20px 4px 4px 20px',
          }}>
          Green
        </button>
        <button data-testid="wingo-violet" onClick={() => onBet("color", "violet", "Join Violet", mults.violet || 4.5)}
          className="py-3 sm:py-3.5 text-white font-semibold text-sm sm:text-base active:scale-95 transition"
          style={{
            background:'#a855f7',
            borderRadius:'4px',
          }}>
          Violet
        </button>
        <button data-testid="wingo-red" onClick={() => onBet("color", "red", "Join Red", mults.red || 2)}
          className="py-3 sm:py-3.5 text-white font-semibold text-sm sm:text-base active:scale-95 transition"
          style={{
            background:'#ef4444',
            borderRadius:'4px 20px 20px 4px',
          }}>
          Red
        </button>
      </div>

      {/* Numbers 0-9 */}
      <div className="grid grid-cols-5 gap-1.5 sm:gap-2 mb-3">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <BigBall key={n} n={n}
            testid={`wingo-num-${n}`}
            onClick={() => onBet("number", n, `Number ${n}`, mults.number || 9)}
          />
        ))}
      </div>

      {/* Multiplier chips — horizontal scroll with soft right-edge fade to hint scrolling */}
      <div className="relative mb-3">
        <div
          className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin"
          style={{ scrollbarWidth: "thin" }}
        >
          {MULT_CHIPS.map((c) => {
            const active = multChip === c.value;
            const isRand = c.value === "rand";
            const base = "flex-shrink-0 px-2.5 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-bold transition";
            if (isRand) {
              return (
                <button key={c.value} onClick={() => setMultChip(c.value)}
                  data-testid={`mult-chip-${c.value}`}
                  className={`${base} bg-white text-rose-500 ${
                    active ? "border-2 border-rose-400" : "border-2 border-rose-200"
                  }`}
                >
                  {c.label}
                </button>
              );
            }
            return (
              <button key={c.value} onClick={() => setMultChip(c.value)}
                data-testid={`mult-chip-${c.value}`}
                className={`${base} ${
                  active
                    ? "bg-emerald-500 text-white"
                    : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
        {/* Right-edge fade → tells the user this scrolls sideways */}
        {isMobile && (
          <div
            aria-hidden
            className="pointer-events-none absolute top-0 right-0 h-full w-8"
            style={{ background: "linear-gradient(90deg, transparent, #ffffff 80%)" }}
          />
        )}
      </div>

      {/* Big / Small — pill split like reference */}
      <div className="flex rounded-full overflow-hidden">
        <button data-testid="wingo-big" onClick={() => onBet("size", "big", "Big (5-9)", mults.big || 2)}
          className="flex-1 py-3 sm:py-3.5 text-white font-semibold text-sm sm:text-base active:scale-95 transition"
          style={{background:'#f59e0b'}}>
          Big
        </button>
        <button data-testid="wingo-small" onClick={() => onBet("size", "small", "Small (0-4)", mults.small || 2)}
          className="flex-1 py-3 sm:py-3.5 text-white font-semibold text-sm sm:text-base active:scale-95 transition"
          style={{background:'#60a5fa'}}>
          Small
        </button>
      </div>
    </div>
  );
}

// ═════════ K3 ═════════
function DiceSVG({ v }) {
  // Simple 3D dice SVG
  const dots = {
    1: [[50, 50]],
    2: [[30, 30], [70, 70]],
    3: [[30, 30], [50, 50], [70, 70]],
    4: [[30, 30], [30, 70], [70, 30], [70, 70]],
    5: [[30, 30], [30, 70], [50, 50], [70, 30], [70, 70]],
    6: [[30, 25], [30, 50], [30, 75], [70, 25], [70, 50], [70, 75]],
  };
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <defs>
        <linearGradient id={`d-${v}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ff6b6b" />
          <stop offset="100%" stopColor="#c81c30" />
        </linearGradient>
      </defs>
      <rect x="8" y="8" width="84" height="84" rx="14" fill={`url(#d-${v})`} />
      {(dots[v] || dots[1]).map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="7" fill={i === 0 && v === 1 ? "#ffd449" : "#fff"} />
      ))}
    </svg>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Hollow/outlined lottery ball (matches WinGo-K3 reference exactly)
// ═════════════════════════════════════════════════════════════════════
const BALL_PALETTES = {
  rose: {
    ring:  "#e63946",
    text:  "#c1272d",
    fillL: "#ffe9ee",
    fillD: "#ffc9d1",
  },
  emerald: {
    ring:  "#1fb95d",
    text:  "#0f9d58",
    fillL: "#e6faec",
    fillD: "#c9ecd6",
  },
};

function LotteryBall({ n, color = "rose", size = 48 }) {
  const p = BALL_PALETTES[color] || BALL_PALETTES.rose;
  const s = size;
  return (
    <div
      className="relative inline-flex items-center justify-center rounded-full leading-none select-none"
      style={{
        width: s,
        height: s,
        background: `radial-gradient(circle at 32% 25%, #ffffff 0%, ${p.fillL} 42%, ${p.fillD} 100%)`,
        border: `${Math.max(2, s * 0.055)}px solid ${p.ring}`,
        boxShadow: `
          0 3px 7px -2px ${p.ring}55,
          inset 0 -4px 8px -3px ${p.ring}40,
          inset 0 2px 4px -1px #ffffff
        `,
        color: p.text,
      }}
    >
      <span
        className="font-black tabular-nums"
        style={{
          fontSize: s * 0.5,
          letterSpacing: "-0.03em",
          textShadow: `0 1px 0 #ffffff`,
        }}
      >
        {n}
      </span>
      {/* Subtle top-left specular highlight */}
      <span
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          top: `${s * 0.15}px`,
          left: `${s * 0.24}px`,
          width: `${s * 0.22}px`,
          height: `${s * 0.11}px`,
          borderRadius: "50%",
          background: "radial-gradient(ellipse at center, #ffffff 0%, transparent 70%)",
          opacity: 0.9,
        }}
      />
    </div>
  );
}

function MultChip({ x }) {
  return (
    <span className="text-slate-500 text-[12px] font-bold tabular-nums tracking-tight">
      {Number(x).toFixed(2)}X
    </span>
  );
}

function K3Panel({ mults, canBet, onBet, lastDice }) {
  const sums = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
  const [subtab, setSubtab] = useState("total");
  const dice = lastDice && lastDice.length === 3 ? lastDice : [3, 1, 5];
  const isMobile = useIsMobile();
  const ballSize = isMobile ? 42 : 54;

  const SegTab = ({ id, label }) => (
    <button
      key={id}
      onClick={() => setSubtab(id)}
      data-testid={`k3-subtab-${id}`}
      className={`py-2.5 rounded-md font-bold text-sm transition-colors ${
        subtab === id
          ? "bg-[#ff5a70] text-white"
          : "bg-[#eef0f2] text-slate-500 hover:text-slate-700"
      }`}
    >
      {label}
    </button>
  );

  return (
    <>
      {/* Dice display — green casino frame with side handles */}
      <div className="relative mb-4">
        {/* Left handle */}
        <span className="absolute top-1/2 -translate-y-1/2 -left-1 w-3 h-10 rounded-l-md bg-[#1fb95d]" />
        {/* Right handle */}
        <span className="absolute top-1/2 -translate-y-1/2 -right-1 w-3 h-10 rounded-r-md bg-[#1fb95d]" />
        <div className="relative rounded-2xl p-1.5 bg-[#1fb95d]">
          <div className="rounded-xl p-3 flex items-center justify-around gap-2 h-28"
               style={{background:'linear-gradient(180deg, #d4d4d8 0%, #a1a1aa 100%)'}}>
            <div className="w-20 h-20"><DiceSVG v={dice[0]} /></div>
            <div className="w-20 h-20"><DiceSVG v={dice[1]} /></div>
            <div className="w-20 h-20"><DiceSVG v={dice[2]} /></div>
          </div>
        </div>
      </div>

      {/* Segmented sub-tabs — flat rectangles matching reference */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <SegTab id="total" label="Total" />
        <SegTab id="2same" label="2 same" />
        <SegTab id="3same" label="3 same" />
        <SegTab id="diff"  label="Different" />
      </div>

      {subtab === "total" && (
        <>
          {/* Sum board — plain grid, no card wrapper (reference has no card) */}
          <div className="grid grid-cols-4 gap-x-2 gap-y-3 mb-4">
              {sums.map((s) => {
                const m = mults[`sum_${s}`] || 0;
                const isEven = s % 2 === 0;
                return (
                  <button
                    key={s}
                    onClick={() => canBet && onBet("sum", s, `Sum ${s}`, m)}
                    className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                    data-testid={`k3-sum-${s}`}
                  >
                    <LotteryBall n={s} color={isEven ? "emerald" : "rose"} size={52} />
                    <MultChip x={m} />
                  </button>
                );
              })}
          </div>
          <div className="grid grid-cols-4 gap-2">
            <button data-testid="k3-small" onClick={() => canBet && onBet("size", "small", "Small (4-10)", mults.small || 2)}
              className="py-2.5 rounded-md bg-[#3aa5f7] text-white font-black text-base leading-tight active:scale-[0.98] transition-transform">
              Small<br/><span className="text-xs font-bold opacity-95">2X</span>
            </button>
            <button data-testid="k3-big" onClick={() => canBet && onBet("size", "big", "Big (11-17)", mults.big || 2)}
              className="py-2.5 rounded-md bg-[#f5a53a] text-white font-black text-base leading-tight active:scale-[0.98] transition-transform">
              Big<br/><span className="text-xs font-bold opacity-95">2X</span>
            </button>
            <button data-testid="k3-even" onClick={() => canBet && onBet("parity", "even", "Even", mults.even || 2)}
              className="py-2.5 rounded-md bg-[#1fb95d] text-white font-black text-base leading-tight active:scale-[0.98] transition-transform">
              Even<br/><span className="text-xs font-bold opacity-95">2X</span>
            </button>
            <button data-testid="k3-odd" onClick={() => canBet && onBet("parity", "odd", "Odd", mults.odd || 2)}
              className="py-2.5 rounded-md bg-[#e63946] text-white font-black text-base leading-tight active:scale-[0.98] transition-transform">
              Odd<br/><span className="text-xs font-bold opacity-95">2X</span>
            </button>
          </div>
        </>
      )}

      {subtab === "2same" && (
        <div className="grid grid-cols-3 gap-2">
          <button data-testid="k3-any-pair" onClick={() => canBet && onBet("any_pair", "any", "Any pair", mults.any_pair || 13.83)}
            className="py-3 rounded-xl bg-gradient-to-b from-indigo-400 to-indigo-600 text-white font-black shadow-md active:scale-[0.98] transition-transform">
            Any pair<br/><span className="text-[11px] font-bold opacity-90 tabular-nums">{(mults.any_pair || 13.83).toFixed(2)}×</span>
          </button>
          <div className="col-span-2 text-xs text-slate-400 flex items-center justify-center">
            Two dice show the same value
          </div>
        </div>
      )}

      {subtab === "3same" && (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <button data-testid="k3-any-triple" onClick={() => canBet && onBet("any_triple", "any", "Any triple", mults.any_triple || 34.5)}
              className="py-3 rounded-xl bg-gradient-to-b from-fuchsia-400 to-fuchsia-600 text-white font-black col-span-3 shadow-md active:scale-[0.98] transition-transform">
              Any 3-of-a-kind × <span className="tabular-nums">{(mults.any_triple || 34.5).toFixed(2)}</span>
            </button>
          </div>
          <div className="text-xs text-slate-500 mb-1.5 font-bold uppercase tracking-wider">Specific triple</div>
          <div className="grid grid-cols-6 gap-2">
            {[1, 2, 3, 4, 5, 6].map((d) => (
              <button key={d} data-testid={`k3-triple-${d}`}
                onClick={() => canBet && onBet("specific_triple", d, `Triple ${d}${d}${d}`, mults.specific_triple || 207)}
                className="py-2 rounded-lg bg-gradient-to-b from-rose-400 to-rose-600 text-white font-black shadow active:scale-95 transition-transform tabular-nums">
                {d}{d}{d}
              </button>
            ))}
          </div>
        </>
      )}

      {subtab === "diff" && (
        <div className="text-xs text-slate-400 text-center py-6">
          Betting on "all different" is included in the Sum plays above.
        </div>
      )}
    </>
  );
}

// ═════════ 5D ═════════
// 5D uses SOLID multi-colour 3D balls with vivid saturated palette
const FIVED_PALETTES = {
  red:    { top: "#ffd5db", mid: "#ff3d3d", bot: "#a30000" },
  purple: { top: "#ecd3ff", mid: "#a020f0", bot: "#4a007f" },
  orange: { top: "#ffe0b8", mid: "#ff8a00", bot: "#a03e00" },
  green:  { top: "#c8f7dc", mid: "#00c853", bot: "#005e2b" },
  blue:   { top: "#c8dfff", mid: "#1e6cff", bot: "#0a2a80" },
  violet: { top: "#dccaff", mid: "#7c3aed", bot: "#320d80" },
  brown:  { top: "#f0d4a5", mid: "#c26a13", bot: "#5e2f00" },
  teal:   { top: "#b7fbee", mid: "#00b3a4", bot: "#004d47" },
  indigo: { top: "#c2c9ff", mid: "#3f3ee0", bot: "#151586" },
  crimson:{ top: "#ffbbb0", mid: "#ff2c2c", bot: "#8f0000" },
};

// One distinct colour per digit 0-9 (matches the WinGo/5D reference palette)
const FIVED_DIGIT_COLORS = [
  "red", "purple", "orange", "green", "blue",
  "violet", "brown", "teal", "indigo", "crimson",
];

function FiveDBall({ n, size = 60 }) {
  const p = FIVED_PALETTES[FIVED_DIGIT_COLORS[n]] || FIVED_PALETTES.red;
  const s = size;
  return (
    <div
      className="relative inline-flex items-center justify-center rounded-full text-white font-black leading-none select-none"
      style={{
        width: s, height: s,
        background: `radial-gradient(circle at 34% 26%, #ffffff 0%, ${p.top} 18%, ${p.mid} 55%, ${p.bot} 100%)`,
        boxShadow: `
          0 10px 22px -6px ${p.bot}90,
          0 3px 6px -2px ${p.bot}50,
          inset 0 -8px 14px -4px ${p.bot}cc,
          inset 0 6px 12px -3px #ffffff66
        `,
      }}
    >
      <span
        className="relative z-10 tabular-nums"
        style={{
          fontSize: s * 0.46,
          letterSpacing: "-0.03em",
          textShadow: `0 1px 3px ${p.bot}, 0 0 6px ${p.bot}80`,
        }}
      >
        {n}
      </span>
      {/* Larger, brighter specular highlight */}
      <span
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          top: `${s * 0.13}px`,
          left: `${s * 0.22}px`,
          width: `${s * 0.3}px`,
          height: `${s * 0.16}px`,
          borderRadius: "50%",
          background: "radial-gradient(ellipse at center, #ffffff 0%, #ffffff88 40%, transparent 75%)",
          opacity: 1,
          filter: "blur(0.5px)",
        }}
      />
    </div>
  );
}

function FiveDPanel({ mults, canBet, onBet, posSel, setPosSel }) {
  return (
    <>
      {/* Segmented position tabs */}
      <div className="grid grid-cols-5 gap-1 mb-4 p-1 rounded-xl bg-white border border-slate-200 shadow-[0_2px_6px_rgba(15,23,42,0.06)]">
        {["A", "B", "C", "D", "E"].map((p) => (
          <button
            key={p}
            onClick={() => setPosSel(p)}
            data-testid={`5d-pos-${p}`}
            className={`py-2 rounded-lg font-black text-sm transition-all ${
              posSel === p
                ? "bg-gradient-to-b from-emerald-400 to-emerald-600 text-white shadow-[0_6px_14px_-6px_rgba(16,185,129,0.7),inset_0_1px_0_rgba(255,255,255,0.35)]"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            Pos {p}
          </button>
        ))}
      </div>

      {/* Multi-colour ball grid */}
      <div className="mb-4 rounded-2xl bg-white border border-slate-200 shadow-[0_4px_16px_-6px_rgba(15,23,42,0.08)] p-4">
        <div className="text-[13px] text-slate-500 mb-3">
          Select 1st number <span className="text-[#ff5a70] font-bold">(Odds {(mults.pos_number || 9).toFixed(1)}X)</span>
        </div>
        <div className="grid grid-cols-5 gap-3 justify-items-center">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button
              key={n}
              data-testid={`5d-${posSel}-${n}`}
              onClick={() => canBet && onBet("position", `${posSel}:${n}`, `${posSel}=${n}`, mults.pos_number || 9)}
              className="active:scale-95 transition-transform"
            >
              <FiveDBall n={n} size={60} />
            </button>
          ))}
        </div>
      </div>

      {/* Big / Small / Odd / Even */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <button onClick={() => canBet && onBet("position", `${posSel}:big`, `${posSel} Big`, mults.pos_big || 2)}
          className="py-2.5 rounded-xl bg-gradient-to-b from-amber-400 to-amber-600 text-white font-black shadow-[inset_0_-4px_8px_-2px_rgba(0,0,0,0.25),inset_0_2px_4px_-1px_rgba(255,255,255,0.35)] active:scale-[0.98] transition-transform">
          Big <span className="text-[10px] font-bold opacity-90 ml-1 tabular-nums">{(mults.pos_big || 2).toFixed(2)}×</span>
        </button>
        <button onClick={() => canBet && onBet("position", `${posSel}:small`, `${posSel} Small`, mults.pos_small || 2)}
          className="py-2.5 rounded-xl bg-gradient-to-b from-sky-400 to-sky-600 text-white font-black shadow-[inset_0_-4px_8px_-2px_rgba(0,0,0,0.25),inset_0_2px_4px_-1px_rgba(255,255,255,0.35)] active:scale-[0.98] transition-transform">
          Small <span className="text-[10px] font-bold opacity-90 ml-1 tabular-nums">{(mults.pos_small || 2).toFixed(2)}×</span>
        </button>
        <button onClick={() => canBet && onBet("position", `${posSel}:odd`, `${posSel} Odd`, mults.pos_odd || 2)}
          className="py-2.5 rounded-xl bg-gradient-to-b from-emerald-400 to-emerald-600 text-white font-black shadow-[inset_0_-4px_8px_-2px_rgba(0,0,0,0.25),inset_0_2px_4px_-1px_rgba(255,255,255,0.35)] active:scale-[0.98] transition-transform">
          Odd <span className="text-[10px] font-bold opacity-90 ml-1 tabular-nums">{(mults.pos_odd || 2).toFixed(2)}×</span>
        </button>
        <button onClick={() => canBet && onBet("position", `${posSel}:even`, `${posSel} Even`, mults.pos_even || 2)}
          className="py-2.5 rounded-xl bg-gradient-to-b from-rose-400 to-rose-600 text-white font-black shadow-[inset_0_-4px_8px_-2px_rgba(0,0,0,0.25),inset_0_2px_4px_-1px_rgba(255,255,255,0.35)] active:scale-[0.98] transition-transform">
          Even <span className="text-[10px] font-bold opacity-90 ml-1 tabular-nums">{(mults.pos_even || 2).toFixed(2)}×</span>
        </button>
      </div>

      <div className="text-[11px] text-slate-500 mb-1.5 font-black uppercase tracking-[0.14em]">Sum</div>
      <div className="grid grid-cols-4 gap-2">
        <button onClick={() => canBet && onBet("sum", "big", "Sum Big (23-45)", mults.sum_big || 2)}
          className="py-2.5 rounded-xl bg-gradient-to-b from-amber-500 to-amber-700 text-white font-black shadow-[inset_0_-4px_8px_-2px_rgba(0,0,0,0.25),inset_0_2px_4px_-1px_rgba(255,255,255,0.35)] active:scale-[0.98] transition-transform">
          Sum Big
        </button>
        <button onClick={() => canBet && onBet("sum", "small", "Sum Small (0-22)", mults.sum_small || 2)}
          className="py-2.5 rounded-xl bg-gradient-to-b from-sky-500 to-sky-700 text-white font-black shadow-[inset_0_-4px_8px_-2px_rgba(0,0,0,0.25),inset_0_2px_4px_-1px_rgba(255,255,255,0.35)] active:scale-[0.98] transition-transform">
          Sum Small
        </button>
        <button onClick={() => canBet && onBet("sum", "odd", "Sum Odd", mults.sum_odd || 2)}
          className="py-2.5 rounded-xl bg-gradient-to-b from-emerald-500 to-emerald-700 text-white font-black shadow-[inset_0_-4px_8px_-2px_rgba(0,0,0,0.25),inset_0_2px_4px_-1px_rgba(255,255,255,0.35)] active:scale-[0.98] transition-transform">
          Sum Odd
        </button>
        <button onClick={() => canBet && onBet("sum", "even", "Sum Even", mults.sum_even || 2)}
          className="py-2.5 rounded-xl bg-gradient-to-b from-rose-500 to-rose-700 text-white font-black shadow-[inset_0_-4px_8px_-2px_rgba(0,0,0,0.25),inset_0_2px_4px_-1px_rgba(255,255,255,0.35)] active:scale-[0.98] transition-transform">
          Sum Even
        </button>
      </div>
    </>
  );
}

// ═════════ History Row ═════════
// ─── Pagination footer (1, 2, 3 style with prev/next) ─────────────────
function Pagination({ page, totalPages, onChange, testidPrefix }) {
  if (!totalPages || totalPages <= 1) return null;
  const nums = [];
  for (let i = 1; i <= totalPages; i++) nums.push(i);
  return (
    <div className="flex items-center justify-center gap-1.5 py-3 bg-white border-t border-slate-100" data-testid={`${testidPrefix}-pagination`}>
      <button
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        data-testid={`${testidPrefix}-page-prev`}
        className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
          page <= 1 ? "bg-slate-100 text-slate-300" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
        }`}
      >‹</button>
      {nums.map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          data-testid={`${testidPrefix}-page-${n}`}
          className={`min-w-[32px] h-8 px-2 rounded-lg text-sm font-bold ${
            n === page ? "bg-gradient-to-b from-[#ff4d67] to-[#ff7a8c] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
          }`}
        >{n}</button>
      ))}
      <button
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        data-testid={`${testidPrefix}-page-next`}
        className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
          page >= totalPages ? "bg-slate-100 text-slate-300" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
        }`}
      >›</button>
    </div>
  );
}

function HistoryRow({ h, game, isTRX }) {
  const r = h.result || {};
  const t = new Date(h.settled_at || h.end_at);
  const fullPeriod = formatPeriod({ start_at: h.start_at || h.settled_at || h.end_at, period: h.period, duration: h.duration }, game);
  // Period cell needs `min-w-0` so it can shrink under grid, and responsive size
  const periodCls = "font-mono text-[10.5px] sm:text-[13px] text-slate-700 truncate min-w-0";
  if (isTRX) {
    const hash = r.hash || "";
    return (
      <div className="grid grid-cols-5 gap-2 px-3 py-3 border-b border-slate-100 items-center text-sm">
        <div className={periodCls}>{fullPeriod}</div>
        <div className="text-slate-500 text-xs">?</div>
        <div className="text-slate-500 text-xs">{pad(t.getUTCHours())}:{pad(t.getUTCMinutes())}:{pad(t.getUTCSeconds())}</div>
        <div className="font-mono text-slate-500 text-xs">**{hash.slice(-4)}</div>
        <div className="text-right flex items-center justify-end gap-1">
          <MiniBall n={r.number} />
          <span className={`text-xs font-bold ${r.size === "big" ? "text-orange-500" : "text-sky-500"}`}>{r.size === "big" ? "B" : "S"}</span>
        </div>
      </div>
    );
  }
  if (game === "k3") {
    return (
      <div
        className="grid gap-2 px-3 py-3 border-b border-slate-100 items-center text-sm"
        style={{ gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr) minmax(0,0.5fr) minmax(0,1.1fr)" }}
      >
        <div className={periodCls}>{fullPeriod}</div>
        <div className="flex gap-1">
          {(r.dice || []).map((d, i) => <span key={i} className="inline-block w-5 h-5 bg-rose-500 text-white rounded text-xs text-center leading-5 font-bold">{d}</span>)}
        </div>
        <div className="text-slate-800 font-bold">{r.sum}</div>
        <div className="text-right text-xs whitespace-nowrap">
          <span className={`font-bold ${r.size === "big" ? "text-amber-500" : "text-sky-500"}`}>{r.size?.toUpperCase()}</span>
          <span className="text-slate-400 mx-1">·</span>
          <span className={`font-bold ${r.parity === "even" ? "text-emerald-500" : "text-rose-500"}`}>{r.parity?.toUpperCase()}</span>
        </div>
      </div>
    );
  }
  if (game === "5d") {
    return (
      <div
        className="grid gap-2 px-3 py-3 border-b border-slate-100 items-center text-sm"
        style={{ gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1.3fr) minmax(0,0.5fr)" }}
      >
        <div className={periodCls}>{fullPeriod}</div>
        <div className="flex gap-1">
          {(r.digits || []).map((d, i) => <span key={i} className="inline-block w-5 h-5 bg-emerald-500 text-white rounded text-xs text-center leading-5 font-bold">{d}</span>)}
        </div>
        <div className="text-right text-slate-800 font-bold whitespace-nowrap">Σ{r.sum}</div>
      </div>
    );
  }
  return (
    <div
      className="grid gap-2 px-3 py-3 border-b border-slate-100 items-center text-sm"
      style={{ gridTemplateColumns: "minmax(0,1.4fr) minmax(0,0.6fr) minmax(0,0.9fr) minmax(0,0.6fr)" }}
    >
      <div className={periodCls}>{fullPeriod}</div>
      <div className="text-center">
        <span className={`font-black text-xl ${
          (NUM_COLORS[r.number] || []).includes("violet") && (NUM_COLORS[r.number] || []).length === 2
            ? "text-fuchsia-600"
            : (NUM_COLORS[r.number] || [])[0] === "green" ? "text-emerald-600" : "text-rose-500"
        }`}>{r.number}</span>
      </div>
      <div className="text-center text-sm text-slate-700">{r.size === "big" ? "Big" : "Small"}</div>
      <div className="text-right flex items-center justify-end gap-1">
        {(NUM_COLORS[r.number] || []).map((c, i) => (
          <span key={i} className={`inline-block w-3 h-3 rounded-full ${
            c === "red" ? "bg-rose-500" : c === "green" ? "bg-emerald-500" : "bg-fuchsia-500"
          }`} />
        ))}
      </div>
    </div>
  );
}
